/**
 * Rider: proportions, body english, and the two-bone IK that puts the hands on the
 * grips and the feet on the pedals every frame.
 *
 * OWNER: piece:rider
 *
 * Six things in here are load-bearing and easy to get wrong.
 *
 * 1. **The limbs are solved against the live cockpit, not against the rider.** The bar
 *    and pedal anchors are read out of the bike's own scene graph after it has been
 *    posed — after steering, after fork compression, after the swingarm has moved — so
 *    a hand cannot drift off a grip and a foot cannot slide on a pedal. There is no
 *    animation clip anywhere in this file.
 *
 * 2. **The pose is a chain of constraints, not a set of keyframes.** The sim says where
 *    the rider's mass is (riderFore, riderLat, bodySquash, lean) and the geometry says
 *    where the hands have to end up; the torso angle is whatever reconciles the two.
 *    That is why weight-back on a steep pitch automatically flattens the back and
 *    extends the arms, and why dropping into a compression automatically folds the
 *    legs — none of that is authored, it falls out of the reach solve.
 *
 * 3. **Counter-lean is not a constant.** A rider stays upright relative to the *world*
 *    in a flat corner and lies down with the bike in a berm, and the difference between
 *    those two is entirely how much of the cornering force the ground is already
 *    holding. The bank angle under the tires is that number, and it is in the sim's own
 *    ground samples, so counter-lean is read off the mountain rather than guessed.
 *
 * 4. **The limbs are springs, and that is the only reason load is visible.** Both rider
 *    anchors are *fixed in chassis space*: the grips hang off the steerer and the pedals
 *    off the bottom bracket, so neither one moves when the suspension strokes. A rig
 *    posed purely from cockpit geometry is therefore bit-for-bit identical with the fork
 *    topped out in the air and with it buried at 157 of 170 mm under 1.66 g, which is
 *    exactly what the wave-2 critique caught. The fix is not to fake the anchors —
 *    that would slide the hands off the grips — but to model what actually differs:
 *    four limb springs carrying the rider's mass, driven by the loads the sim already
 *    computes. Under load the body sinks into the bike and every joint folds; unloaded,
 *    it rises and the limbs come straight. See LIMB below.
 *
 * 5. **A value break has to be a FORM break.** This is the wave-3 gap and it is the
 *    whole project's gap stated on one model: "every value break on the rider is a
 *    hard-edged colour patch painted onto a smooth primitive instead of a form edge —
 *    the near-black pack and the near-black shorts seat are flat decals on a teal sphere
 *    and a grey sphere only 11 luma apart, and from the game's own overhead chase angle
 *    they merge into one dark stripe down a mannequin built from separate balls."
 *
 *    Three answers, all structural rather than tonal:
 *
 *    - **The pack is a pack.** It stands 55 mm off the jersey with a lid that overhangs,
 *      a bottom that tapers away from the back, compression straps that wrap it and
 *      shoulder straps that run over the deltoids and down the chest. It breaks the
 *      silhouette in profile and it puts a hard occluded gap between itself and the
 *      back, which is what makes it read when the whole rear of the rider is in shadow.
 *    - **The seat is two panels with a seam between them.** Not one egg with a lighter
 *      stripe painted down it: two flattened panels splayed apart, a 30 mm valley
 *      between them, a flatlock welt sitting down in that valley and a yoke seam across
 *      the top. The eye reads a garment because garments are made of panels.
 *    - **The deltoids are part of the torso.** The rider is now ONE skinned mesh, and
 *      the shoulder cap is weighted across the torso and upper-arm bones, so the mass
 *      deforms continuously instead of being a ball that pivots inside another ball.
 *      Same at the hip. That is what stops it reading as an assembly of primitives.
 *
 * 6. **One mesh, one draw call.** The wave-2 integrator's audit put bike + rider at 46
 *    draw calls from 26 primitive meshes, each drawn for beauty and again for shadow —
 *    the biggest single line in the game. Every rigid group is now a *bone*, all of the
 *    geometry is merged into one buffer with colour, roughness and baked occlusion in
 *    the vertices, and the whole kit is a single `THREE.SkinnedMesh`. See `Skin`.
 *
 * 6b. **A garment needs a SILHOUETTE, and only a loft can give it one.** The round-1
 *    version of (5) fixed the upper body and left the lower body untouched, and the
 *    critique that came back named it exactly: "each thigh is a straight-walled cylinder
 *    the same diameter at hip and cuff, ending flat at mid-thigh with no drape, no flare
 *    and no continuity into the hip, so the legs read as a stack of disconnected
 *    primitives — dark seat ellipsoid, grey pipe, bare stick, pale bead at the knee,
 *    stick, shoe."
 *
 *    That list is six items and every one of them is an *outline*, so no palette or
 *    shading move could have answered it. `PartBuilder.loft` exists for this: a stack of
 *    cross-sections with per-ring drift, drape and folds, which is the smallest primitive
 *    that can be tucked at the hip, close at mid-thigh, flared at the hem, and finished
 *    with an edge that hangs 19 mm lower at the outer front than at the inner back. The
 *    short is now ONE mass across a 0.296 m hip that forks into two flared legs, the hem
 *    drops over the knee pad instead of stopping at 55% of the thigh, and it is panelled
 *    — side seam, inseam, seat yoke, thigh pocket — with every seam placed by evaluating
 *    the same surface function the cloth was built from, so nothing floats.
 *
 * 6c. **THE ARCHETYPE IS THE GARMENT LOGIC.** This is the wave-3 round-3 gap and it was
 *    stated as one thing, correctly: "the four bare-skin limb cylinders — both forearms
 *    and both shins — are the last untreated primitives on the rider, and the cause is
 *    that BAR.md's decided archetype was not built."
 *
 *    BAR specifies **long trousers, not baggy shorts, with knee pads underneath**, and **a
 *    sleeveless or short-sleeve jersey worn over a long-sleeve base layer**. Round 2 built
 *    the opposite of the first clause and skipped the second, and the consequence was
 *    structural rather than stylistic: with the short ending at 73% of the thigh and the
 *    jersey sleeve handing over at 47% of the upper arm, everything past those two points
 *    had to be either skin or armour. There was no garment left to put on it. So the four
 *    cylinders were not an oversight that shading could have covered — they were the only
 *    possible consequence of the wrong kit.
 *
 *    Building the archetype closes it at the source, and the same move closes the torso's
 *    single-layer read, which is why it was one gap and not three:
 *
 *    - **The trouser runs waistband to ankle.** The thigh loft carries on past the old hem
 *      to 85 mm above the knee; the shin part carries a knee section and a lower leg, in
 *      two panels with a welt between them, a calf that drifts 5 mm rearward and swells,
 *      and a rolled cuff with an adjuster tab. The knee pad is *under* it, read as the
 *      bulge the knee section is built around and as the 23-luma-darker panel over it.
 *    - **The knee is a JOINT.** `kneeRamp` hands the top 88 mm of the shin part to the
 *      thigh bone, so the cloth bends through the knee instead of two tubes telescoping.
 *      Same at the elbow (`elbowRamp`). Before this, the shoulder was the only blended
 *      joint on the model, which is most of why everything below it read as a stack.
 *    - **The sleeve ENDS and the base layer runs out from under it.** A 4 mm flare at the
 *      hem with an occlusion crease under it, then a lofted base-layer sleeve down past the
 *      elbow to a wrist rib and the glove. The layer edge is carried by form and hue, not
 *      by value — see `K.base`, whose first value was chosen for the swatch and measured at
 *      21.5 luma on the actual arm, fused with the glove.
 *    - **The collar shows both layers at once**, which is the only 60 mm on the model where
 *      a viewer can see that there are two.
 *
 *    There is no `K.skin` left anywhere a camera can reach it.
 *
 * 6d. **The shoulder cap is a loft that starts inside the torso.** Round 1 weighted the
 *    deltoid across two bones and it still read as "two teal balls flanking a teal torso"
 *    from dead astern, because blending the *skinning* does nothing about the *surface*:
 *    an ellipsoid resting on an ellipsoid leaves an intersection curve, and that curve at
 *    the shoulder is exactly the crease the eye calls "assembled balls". A loft has no
 *    intersection to leave. It now runs from a ring buried near the base of the neck, out
 *    over the trapezius, through the deltoid and into the sleeve as one surface.
 *
 * 6e. **TWO LEGS, NOT A SACK — and the fix is a HOLE, not a highlight.** This is the
 *    wave-3 carried gap and the wave-4 job: "from the waist to the knee the rider is one
 *    smooth inflated khaki volume: the seat, both thighs and the pack all sit in the same
 *    warm-graphite band with no inseam value break and no background gap between the legs,
 *    so below the jersey the rider reads as a sack rather than two legs at every camera
 *    angle."
 *
 *    Measured on the wave-3 build before anything was changed, the two thigh lofts were
 *    0.180 m apart (`hipHalf * 0.9`) and each was 0.172 m wide, so **their inner faces met
 *    at 4 mm from the centreline and then intersected**, and a third ellipsoid 0.212 m
 *    across sat under the seat spanning from one leg to the other. There was no gap to
 *    see because there was no gap: no shading move could ever have produced one.
 *
 *    Four changes, in the order they matter:
 *
 *    - **The bridge is gone.** The ellipsoid that spanned the crotch is deleted. What
 *      closes the top of the garment now is the hip and seat masses being narrow enough to
 *      do it on their own, and below torso y = -0.075 there is nothing at all.
 *    - **The section is narrowed laterally and the bones splayed.** `rz` drops from 0.086
 *      to 0.065 while `rx` is untouched, the section drifts 8–14 mm outboard as it
 *      descends, and `POSE.hipSplay` goes from 0.090 to 0.098. Measured on the shipped
 *      buffer (`captures/w4-rider-view.mjs`, `legGap`), that is **80–200 mm of clear air
 *      between the two trouser legs from the crotch to the ankle**, against 8 mm before.
 *    - **The inner face is its own panel.** Each leg loft is built as two arcs sharing
 *      their edges — a gusset over the inner face in `trouserIn`, the field over the rest
 *      — with a welt down both joins. The value break is a panel boundary on a real seam,
 *      and it lands on exactly the two faces that look at each other across the new gap.
 *    - **The palette collapses to two fields.** See `K`. Jersey to trouser goes from 27
 *      luma to 114.
 *
 *    Verified in the frame the player sees, not in a swatch: the beauty render from dead
 *    astern now has near-black legs either side of a 179–194 luma column of frame, at
 *    every height from the upper thigh to the knee. Before, that scanline was one
 *    continuous mid band.
 *
 * 6f. **A HEAD, NOT A HAT — and the fix is on the BACK of the shell.** This is the wave-4
 *    round-3 gap and it is the first one on this piece that was about the image rather than
 *    the model: "helmet-only luma in the delivered parked/descend frames is p50 12 / p95 47
 *    against a same-frame torso p50 53, so the 58 mm eyeport recess, 70 mm chin projection
 *    and 58-vs-78 occipital step are true of the mesh and invisible in the image; and
 *    because `K.helmet` and `K.packLid` sit 1.5 luma apart with no lit neck between them,
 *    the shell fuses with the pack lid into one silhouette that reads as a brimmed hat with
 *    a dark bib."
 *
 *    Round 2 built a genuine full face and put every feature of it on the FRONT. The chase
 *    camera never sees the front. Four changes, all on the rear arc or on the neck:
 *
 *    - **The shell's own lower rear edge arches.** The last six loft rings carry a `drop`
 *      with their `y` raised by the same amount, so the chin and the jawline do not move a
 *      millimetre and the nape climbs 8 / 20 / 36 / 36 / 24 / 10 mm. That is the
 *      occiput-to-neck scallop, and it is in the OUTLINE, which is the only place a curve
 *      survives on a surface the sun never reaches.
 *    - **A light centre spine down the occiput**, 144 against the shell's 106, with the two
 *      exhaust ports as narrow dark slots either side of it. Vertical and singular on
 *      purpose: the first cut was a wide arched slot across the back and at chase distance
 *      it broke into two dark marks and read as eyes.
 *    - **The orange is a badge, not a band.** It ran temple to temple, dead level, across
 *      the widest part of the head. That line *was* the brim. It is a 0.86-rad arc now.
 *    - **A neck exists**: helmet 78 -> 106 and packLid 77 -> 63 (1.5 luma apart -> 43), the
 *      pack dropped 45 mm, and a jersey-valued collar whose top face is the one lit
 *      horizontal plane between the shell and the pack.
 *
 *    Measured with `captures/w4-head-view.mjs`, which masks the head exactly by collapsing
 *    the head bone and differencing two silhouettes: from dead astern the head went p50 8
 *    -> 19 against a whole-kit p50 of 64 -> 67 in the same frame, i.e. 12% of the kit's
 *    median to 28% of it. Buffer albedo p50 78 -> 106 with 1.7% of the head's surface over
 *    140, all of it the spine.
 *
 * 7. **Occlusion scales the light, not the paint.** The creases were baked into the
 *    vertex colour, which multiplies albedo — but the surface the chase camera actually
 *    looks at is the rider's back, and the rider's back is lit by ambient. Multiplying
 *    albedo cannot separate two masses when the ambient term is what is flattening them.
 *    The bake now rides in its own attribute and is routed through the shared shading
 *    library's `gRlfOcc`, which scales irradiance. See `createKitMaterial`.
 */

import * as THREE from 'three';
import { registerSystem } from '../core/systems';
import { Tuning } from '../core/tuning';
import type { BikeState } from './physics';
import { clamp, clamp01, damp, lerp } from '../core/math';
import { patchStandardMaterial, installReliefSystem } from '../render/shading/relief';
import { declareInvariant } from '../core/audit';

// ---------------------------------------------------------------------------------
// Self-audit. Wave 5's shared bus (`src/core/audit.ts`) exists because a piece printed
// that it was 147x over its own budget on every boot for four waves and shipped anyway.
// These four are this piece's own budgets, and the first one is the wave-5 carried gap
// stated as a number that a future round cannot quietly undo.
// ---------------------------------------------------------------------------------

/**
 * **The gap, as an invariant.** The helmet's rear silhouette is its half-width `rz` as a
 * function of height; the chase camera sees that and essentially nothing else, because
 * `cx` — which carries the whole 58 mm eyeport recess and the whole 70 mm chin projection
 * — points straight away from it and does not project at all.
 *
 * A half-width that rises once and falls once IS an ellipse. That is exactly what the
 * round-3 shell was, and it is why three rounds of pushing the shell's value could not
 * fix "a featureless dark egg": there was no feature in the outline to light.
 *
 * **This invariant fails on the build that shipped last wave** — that shell has one
 * reversal, this one has three (temple -> ear pocket -> cheek pod -> chin bar). It is a
 * `min` because the failure mode is smoothness, not excess.
 */
const HELMET_REVERSALS = declareInvariant({
  id: 'rider.helmetOutlineReversals',
  owner: 'rider',
  what: 'direction reversals in the helmet half-width down the shell, i.e. corners the chase camera can see',
  unit: 'count',
  budget: 3,
  compare: 'min',
  phase: 'build',
  consequence: 'a monotone outline is an ellipse, so the head reads as a dark egg from behind however it is shaded',
  source: 'src/bike/rider.ts shell[] rz',
});

/**
 * How far the visor peak's outer tip stands outside the shell's OWN section at the peak's
 * height — a local step, not a step over the widest ring anywhere on the head.
 *
 * ### The budget is 45 mm and it is not a taste call
 *
 * Wave 5 round 1 shipped a peak 23 mm proud, declared it against a 12 mm budget, passed,
 * and the critique came back: *"the round's entire delivered change is invisible at the
 * size the player sees the rider."* The 12 mm was invented. This one is measured.
 *
 * `captures/w5-rider-tools/headpx.mjs --scaletest` runs the shared outline metric against
 * synthetic head silhouettes **at the delivered head size** and sweeps the feature size
 * until the metric can separate the shape from a plain egg. The answer, at the 31-68 px
 * the chase camera actually delivers:
 *
 * | feature | smallest size that survives |
 * |---|---|
 * | lateral spur (a peak) | 8 px per side = **45 mm** |
 * | scallop + flare (a jaw) | 6 px per side = **34 mm** |
 * | proud slab (a chin bar) | 10 px = **56 mm** |
 *
 * The thresholds scale with the head, so they are the same millimetres at either end of
 * that range. Round 1's peak was 23 mm and its jaw flare was 19 mm: **both below the
 * resolution limit of the instrument that was used to certify them**, which is exactly
 * why the A/B came back near-identical.
 */
const PEAK_PROUD = declareInvariant({
  id: 'rider.helmetPeakProudMm',
  owner: 'rider',
  what: 'visor peak tip outside the shell section at the peak height',
  unit: 'mm',
  budget: 45,
  compare: 'min',
  phase: 'build',
  consequence: 'a peak under 45 mm proud is under the resolution limit of the delivered frame, so the head reads as an egg however it is shaded',
  source: 'src/bike/rider.ts peak outer ring rz, budget from headpx.mjs --scaletest',
});

/**
 * ...and its mirror at the bottom of the shell: the ear pocket to the cheek pod. Same
 * table, same reason — 34 mm is where a scallop stops being antialiasing at 31-68 px.
 *
 * This is the number the ROUND-1 build failed at 19 mm while `rider.helmetOutlineReversals`
 * reported three healthy reversals, which is the whole lesson: a reversal that exists is
 * not a reversal you can see. Counting corners was never enough; they have to be deep.
 */
const JAW_FLARE = declareInvariant({
  id: 'rider.helmetJawFlareMm',
  owner: 'rider',
  what: 'cheek pod half-width over the ear pocket waist above it',
  unit: 'mm',
  budget: 34,
  compare: 'min',
  phase: 'build',
  consequence: 'a shallower flare is inside the delivered frame\'s noise, so the lower half of the head is an oval',
  source: 'src/bike/rider.ts shell[] rz, budget from headpx.mjs --scaletest',
});

/**
 * The oldest rider defect there is: a hand that is not on the bar it is steering with.
 * The IK clamps an unreachable target rather than stretching the limb, so when body
 * english asks for more than an arm has, the hand ends up short — silently.
 */
const HAND_GAP = declareInvariant({
  id: 'rider.gripHandGapM',
  owner: 'rider',
  what: 'gap between the solved hand and the grip while riding',
  unit: 'm',
  budget: 0.045,
  consequence: 'the hand floats off the bar and the rider stops looking like they are steering',
  source: 'src/bike/rider.ts solveTwoBone / armLim',
});

/**
 * ...and its mirror image, which is the wave-5 coherence gap: while the sim holds
 * `crashed`, the hands must have LEFT the bars. A rider toppling with their arms still
 * out to a cockpit lying on the hillside is the failure this measures, and the player
 * sees it six times a run.
 *
 * Only observed once the fall is past halfway (see the observation site), so it reads
 * `unexercised` on any scenario that does not crash — which, measured this wave, is every
 * scenario the harness ships including `fullrun`. That is the honest state, not a wiring
 * gap, and it is also a finding: the coherence pass's "six times a run" is no longer true
 * of this build. `captures/w5-rider-tools/crashview.mjs` is what exercises it.
 */
const CRASH_LETGO = declareInvariant({
  id: 'rider.crashHandsOffBarM',
  owner: 'rider',
  what: 'distance from the hand to the grip once the fall is past halfway',
  unit: 'm',
  budget: 0.12,
  compare: 'min',
  consequence: 'the rider topples as a rigid mannequin still holding bars that are no longer under it',
  source: 'src/bike/rider.ts CRASH.handFwd',
});

// ---------------------------------------------------------------------------------
// Geometry helper. Every part of the bike and the rider is built from primitives and
// merged into ONE skinned buffer, with colour, surface parameters and baked contact
// occlusion in the vertices. Shared with render/bikeModel.ts, which is the only other
// file that may import it.
// ---------------------------------------------------------------------------------

const _m = new THREE.Matrix4();
const _nm = new THREE.Matrix3();
const _q = new THREE.Quaternion();
const _one = new THREE.Vector3(1, 1, 1);
const _pa = new THREE.Vector3();
const _pb = new THREE.Vector3();
const _pd = new THREE.Vector3();
const _euler = new THREE.Euler();
const _col = new THREE.Color();
// Construction-time only: seam and hem placement reads the loft surface it is laying on.
const _sa = new THREE.Vector3();
const _sb = new THREE.Vector3();
const _sc = new THREE.Vector3();
const ZAXIS = new THREE.Vector3(0, 0, 1);
const XAXIS = new THREE.Vector3(1, 0, 0);
const UP = new THREE.Vector3(0, 1, 0);

/**
 * Ceiling on baked occlusion. Past this a crease reads as a hole — and a hole is not
 * separation, it is the same failure with the sign flipped.
 *
 * Higher than it was, because the bake no longer multiplies albedo: it goes through
 * `gRlfOcc`, which takes indirect light out in full and direct light out at 65%, so the
 * same number darkens a sunlit crease less than the old albedo multiply did and darkens
 * a shaded one — the case that matters — considerably more.
 */
const OCC_MAX = 0.70;

/** One cross-section of a lofted surface. See `PartBuilder.loft`. */
export interface LoftRing {
  /** Height along the part's own local Y. */
  y: number;
  /** Half-width fore–aft (X) and lateral (Z). A garment section is not a circle. */
  rx: number;
  rz: number;
  /** Centre offset, so cloth can hang away from the limb inside it. */
  cx?: number;
  cz?: number;
  /** Drape: the edge dips by `drop` at angle `dropAt` and rides up by it opposite. */
  drop?: number;
  dropAt?: number;
  /** Folds: the radius is scaled by `1 + fold*cos(foldN*theta + foldPhase)`. */
  fold?: number;
  foldN?: number;
  foldPhase?: number;
}

/** Default number of folds around a lofted section. */
const LOFT_FOLD_N = 3;

/**
 * The surface point of a ring at `theta`, pushed `off` metres along its own radius.
 *
 * Shared with the seam and hem code, which is the whole point: a welt laid on a *guess*
 * at where the surface is either floats or sinks, and both read as a decal. Every seam on
 * this rider is placed by evaluating the same function the surface itself was built from.
 */
function loftPoint(R: LoftRing, theta: number, off: number, out: THREE.Vector3): THREE.Vector3 {
  const fold = R.fold ?? 0;
  const n = R.foldN ?? LOFT_FOLD_N;
  const ph = R.foldPhase ?? 0;
  // Two harmonics, not one. A single cosine at N folds puts N evenly spaced bulges round
  // the section, and the eye reads that as a *shape* — four folds made the hem square in
  // the first capture of this. Beating a second harmonic against it costs nothing and
  // the result has no repeat the eye can find.
  const k = fold !== 0
    ? 1 + fold * (0.70 * Math.cos(n * theta + ph) + 0.45 * Math.cos((n + 2) * theta - 1.7 * ph))
    : 1;
  const drop = R.drop ?? 0;
  return out.set(
    (R.cx ?? 0) + (R.rx * k + off) * Math.cos(theta),
    R.y - (drop !== 0 ? drop * Math.cos(theta - (R.dropAt ?? 0)) : 0),
    (R.cz ?? 0) + (R.rz * k + off) * Math.sin(theta),
  );
}

/** Accumulates primitives, then flattens them into one coloured buffer geometry. */
export class PartBuilder {
  private readonly geos: THREE.BufferGeometry[] = [];
  private readonly cols: number[] = [];
  /** Per-primitive surface parameters: roughness, then relief weight. */
  private readonly surfs: number[] = [];
  /** Contact-occlusion probes, five numbers each: x, y, z, radius, strength. */
  private readonly occ: number[] = [];
  private downK = 0;
  private curRough = 0.72;
  private curRelief = 0;

  add(geo: THREE.BufferGeometry, color: number, mtx?: THREE.Matrix4): this {
    if (mtx) geo.applyMatrix4(mtx);
    this.geos.push(geo);
    this.cols.push(color);
    this.surfs.push(this.curRough, this.curRelief);
    return this;
  }

  /**
   * Surface parameters for every primitive added after this call.
   *
   * `rough` is the material roughness; `relief` is how much of the shared library's
   * micro-relief this surface takes, 0 for anything hard and smooth (a rim, a rotor,
   * a goggle lens) and 1 for anything woven. It exists because the whole kit is one
   * material now — thirteen meshes of three materials is thirteen draw calls, and the
   * only thing those materials actually disagreed about was roughness.
   */
  surf(rough: number, relief = 0): this {
    this.curRough = rough;
    this.curRelief = relief;
    return this;
  }

  /**
   * Register a contact-occlusion probe: a sphere that darkens the vertices near it,
   * hardest on the ones whose normals point into it.
   *
   * There is no runtime cost — it is folded into an attribute — and it is the only
   * shading in this build that survives at the one place the sun cannot reach, which is
   * exactly where two masses join. Put one in every armpit, hip crease, hem, seam valley
   * and frame junction: a crease that is 40% darker is what stops a torso, a pair of
   * shorts and a top tube reading as one balloon.
   */
  shade(x: number, y: number, z: number, r: number, k = 0.45): this {
    this.occ.push(x, y, z, r, k);
    return this;
  }

  /**
   * Darken surfaces facing local -Y by up to `k`. Cheap sky occlusion.
   *
   * For the torso, the helmet and the frame, local -Y is world-down and this is
   * literally that. For a limb bone, local -Y runs down the limb, so what it darkens is
   * the distal face of every cuff, hem and pad lip — which is the other half of the
   * same job and is why a hem now reads as an edge instead of as a colour change.
   */
  underShade(k: number): this {
    this.downK = k;
    return this;
  }

  /** Tapered tube running from a to b. r1 is the radius at a, r2 at b. */
  seg(
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    r1: number, color: number, r2 = r1, radial = 8, rings = 1,
  ): this {
    _pa.set(ax, ay, az);
    _pd.set(bx - ax, by - ay, bz - az);
    const len = _pd.length();
    if (len < 1e-5) return this;
    _pd.divideScalar(len);
    _q.setFromUnitVectors(UP, _pd);
    _pa.addScaledVector(_pd, len * 0.5);
    _m.compose(_pa, _q, _one);
    return this.add(new THREE.CylinderGeometry(r2, r1, len, radial, rings), color, _m);
  }

  /** Tube plus rounded ends — reads as a limb, or as a hydroformed frame member. */
  rod(
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    r1: number, color: number, r2 = r1, radial = 8, rings = 1,
  ): this {
    this.seg(ax, ay, az, bx, by, bz, r1, color, r2, radial, rings);
    this.ball(ax, ay, az, r1, color, radial);
    this.ball(bx, by, bz, r2, color, radial);
    return this;
  }

  ball(x: number, y: number, z: number, r: number, color: number, seg = 8): this {
    _pa.set(x, y, z);
    _m.compose(_pa, _q.identity(), _one);
    return this.add(new THREE.SphereGeometry(r, seg, Math.max(4, seg >> 1)), color, _m);
  }

  /** Ellipsoid — the workhorse for readable body masses. */
  blob(
    x: number, y: number, z: number,
    sx: number, sy: number, sz: number,
    color: number, seg = 10, rz = 0, ry = 0,
  ): this {
    _pa.set(x, y, z);
    _q.setFromEuler(_euler.set(0, ry, rz, 'ZYX'));
    _m.compose(_pa, _q, _pb.set(sx, sy, sz));
    return this.add(new THREE.SphereGeometry(1, seg, Math.max(5, seg >> 1)), color, _m);
  }

  box(
    w: number, h: number, d: number,
    x: number, y: number, z: number,
    color: number, rz = 0, ry = 0,
  ): this {
    _pa.set(x, y, z);
    _q.setFromEuler(_euler.set(0, ry, rz, 'ZYX'));
    _m.compose(_pa, _q, _one);
    return this.add(new THREE.BoxGeometry(w, h, d), color, _m);
  }

  /** Ring in the XY plane (the wheel plane), squashed on Z to the width it should be. */
  ring(
    x: number, y: number, z: number,
    major: number, tube: number, width: number,
    color: number, tubeSeg = 6, radialSeg = 24,
  ): this {
    _pa.set(x, y, z);
    _m.compose(_pa, _q.identity(), _pb.set(1, 1, width / Math.max(tube * 2, 1e-4)));
    return this.add(new THREE.TorusGeometry(major, tube, tubeSeg, radialSeg), color, _m);
  }

  /** Cylinder with its axis on Z — hubs, pedal spindles, grip collars. */
  axle(x: number, y: number, z: number, r: number, len: number, color: number, seg = 10): this {
    _pa.set(x, y, z);
    _q.setFromAxisAngle(XAXIS, Math.PI / 2);
    _m.compose(_pa, _q, _one);
    return this.add(new THREE.CylinderGeometry(r, r, len, seg, 1), color, _m);
  }

  /**
   * A lofted surface through a stack of cross-sections. **This is the only primitive in
   * here that can make a garment**, and that is why it exists.
   *
   * An ellipsoid has one radius and a cylinder has two, so neither can be tucked at the
   * hip, close against the leg at mid-thigh, flared at the hem, and finished with an edge
   * that hangs lower on one side than the other. The wave-3 critique measured exactly
   * that absence — "a straight-walled cylinder the same diameter at hip and cuff, ending
   * flat at mid-thigh with no drape, no flare" — and no amount of shading substitutes for
   * it, because what is missing is the *silhouette*.
   *
   * Three shaping terms, all per-ring, all free:
   *
   * - `cx`/`cz` drift the section, so the cloth can hang away from the limb inside it;
   * - `drop`/`dropAt` tilt the ring's edge — the hem hangs lower at the outer front and
   *   rides up at the inner back, which is both what cloth does and what keeps the hem
   *   clear of the shin when the knee folds;
   * - `fold`/`foldN` ripple the radius, which is the cheapest honest drape there is: real
   *   folds, so the sun finds crests and valleys instead of a smooth tube.
   *
   * Rings run in order; the surface orientation follows that order, so a profile may turn
   * back on itself — which is how the hem gets a rolled edge with a lit crest, a shaded
   * under-lip, and an inner wall that closes the opening instead of leaving a hole.
   */
  loft(
    rings: LoftRing[], radial: number, color: number,
    capTop = false, theta0 = 0, theta1 = Math.PI * 2,
  ): this {
    const n = rings.length;
    if (n < 2 || radial < 3) return this;
    const closed = Math.abs(theta1 - theta0 - Math.PI * 2) < 1e-6;
    const cols = closed ? radial : radial + 1;
    const nv = n * cols + (capTop ? 1 : 0);
    const pos = new Float32Array(nv * 3);
    const step = (theta1 - theta0) / radial;
    for (let r = 0; r < n; r++) {
      for (let i = 0; i < cols; i++) {
        loftPoint(rings[r], theta0 + step * i, 0, _pd);
        const o = (r * cols + i) * 3;
        pos[o] = _pd.x; pos[o + 1] = _pd.y; pos[o + 2] = _pd.z;
      }
    }
    const idx: number[] = [];
    for (let r = 0; r < n - 1; r++) {
      const a = r * cols;
      const b = (r + 1) * cols;
      for (let i = 0; i < radial; i++) {
        const j = closed ? (i + 1) % cols : i + 1;
        idx.push(a + i, b + j, b + i, a + i, a + j, b + j);
      }
    }
    if (capTop) {
      const c = n * cols;
      const R = rings[0];
      pos[c * 3] = R.cx ?? 0;
      pos[c * 3 + 1] = R.y;
      pos[c * 3 + 2] = R.cz ?? 0;
      for (let i = 0; i < radial; i++) {
        idx.push(c, closed ? (i + 1) % cols : i + 1, i);
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setIndex(idx);
    g.computeVertexNormals();
    return this.add(g, color);
  }

  build(): THREE.BufferGeometry {
    let vc = 0;
    let ic = 0;
    for (const g of this.geos) {
      if (!g.attributes.normal) g.computeVertexNormals();
      vc += g.attributes.position.count;
      ic += g.index ? g.index.count : g.attributes.position.count;
    }
    const pos = new Float32Array(vc * 3);
    const nrm = new Float32Array(vc * 3);
    const col = new Float32Array(vc * 3);
    const aoc = new Float32Array(vc);
    const srf = new Float32Array(vc * 2);
    const idx = vc > 65000 ? new Uint32Array(ic) : new Uint16Array(ic);
    let vo = 0;
    let io = 0;
    const occ = this.occ;
    const nOcc = occ.length;
    const dk = this.downK;
    for (let k = 0; k < this.geos.length; k++) {
      const g = this.geos[k];
      const p = g.attributes.position;
      const n = g.attributes.normal;
      _col.setHex(this.cols[k], THREE.SRGBColorSpace);
      const cr = _col.r, cg = _col.g, cb = _col.b;
      const sr = this.surfs[k * 2];
      const sl = this.surfs[k * 2 + 1];
      const cnt = p.count;
      for (let i = 0; i < cnt; i++) {
        const o = (vo + i) * 3;
        const px = p.getX(i), py = p.getY(i), pz = p.getZ(i);
        const nx = n.getX(i), ny = n.getY(i), nz = n.getZ(i);
        pos[o] = px; pos[o + 1] = py; pos[o + 2] = pz;
        nrm[o] = nx; nrm[o + 1] = ny; nrm[o + 2] = nz;
        col[o] = cr; col[o + 1] = cg; col[o + 2] = cb;
        srf[(vo + i) * 2] = sr;
        srf[(vo + i) * 2 + 1] = sl;
        let ao = dk > 0 && ny < 0 ? dk * (-ny) * (-ny) : 0;
        for (let j = 0; j < nOcc; j += 5) {
          const dx = occ[j] - px, dy = occ[j + 1] - py, dz = occ[j + 2] - pz;
          const r = occ[j + 3];
          const d2 = dx * dx + dy * dy + dz * dz;
          if (d2 >= r * r) continue;
          const d = Math.sqrt(d2) || 1e-6;
          const t = 1 - d / r;
          // A surface turned toward the occluder is buried; one turned away only loses
          // the sky the occluder is standing in front of, so it keeps most of its value.
          const face = Math.max(0, (dx * nx + dy * ny + dz * nz) / d);
          ao += occ[j + 4] * (t * t * (3 - 2 * t)) * (0.26 + 0.74 * face);
        }
        aoc[vo + i] = ao > 0 ? 1 - Math.min(ao, OCC_MAX) : 1;
      }
      if (g.index) {
        for (let i = 0; i < g.index.count; i++) idx[io + i] = vo + g.index.getX(i);
        io += g.index.count;
      } else {
        for (let i = 0; i < cnt; i++) idx[io + i] = vo + i;
        io += cnt;
      }
      vo += cnt;
      g.dispose();
    }
    const out = new THREE.BufferGeometry();
    out.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    out.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
    out.setAttribute('color', new THREE.BufferAttribute(col, 3));
    out.setAttribute('aOcc', new THREE.BufferAttribute(aoc, 1));
    out.setAttribute('aSurf', new THREE.BufferAttribute(srf, 2));
    out.setIndex(new THREE.BufferAttribute(idx, 1));
    out.computeBoundingSphere();
    this.geos.length = 0;
    this.cols.length = 0;
    this.surfs.length = 0;
    this.occ.length = 0;
    this.downK = 0;
    return out;
  }
}

// ---------------------------------------------------------------------------------
// Skin — one mesh for the whole kit.
//
// Every rigid group that used to be its own `THREE.Group` with its own `THREE.Mesh` is
// now a `THREE.Bone` in one skeleton, and every part's geometry is merged into one
// buffer. The hierarchy, the update code and the authoring coordinates are all
// unchanged: a part is still built in its own group's local frame, and `Skin` bakes that
// group's bind-pose matrix into the vertices as it merges.
//
// The pay-off beyond the draw calls is that a vertex is no longer forced to belong to
// exactly one group. A `SkinRamp` splits it across two, which is the only way to make a
// deltoid part of a torso rather than a ball resting against one.
// ---------------------------------------------------------------------------------

/**
 * A linear blend between two bones across one part.
 *
 * The ramp is measured in the part's OWN authoring frame: project the vertex onto
 * `(dx,dy,dz)` from `(ox,oy,oz)` and the part's own bone takes weight 0 at `d0` and
 * weight 1 at `d1`, with `bone` taking the remainder.
 */
export interface SkinRamp {
  /** Bone that owns the low end of the ramp. */
  bone: number;
  ox: number; oy: number; oz: number;
  dx: number; dy: number; dz: number;
  d0: number; d1: number;
}

interface SkinPart {
  geo: THREE.BufferGeometry;
  bone: number;
  ramp: SkinRamp | null;
}

export class Skin {
  readonly bones: THREE.Bone[] = [];
  private readonly parts: SkinPart[] = [];

  /** Register a bone. It must already be in the graph by the time `build()` runs. */
  bone(b: THREE.Bone): number {
    this.bones.push(b);
    return this.bones.length - 1;
  }

  /** Add geometry authored in `bone`'s own local frame. */
  part(geo: THREE.BufferGeometry, bone: number, ramp: SkinRamp | null = null): void {
    this.parts.push({ geo, bone, ramp });
  }

  /**
   * Merge everything. The caller must have called `updateMatrixWorld(true)` on the root
   * of the bone hierarchy first, with the root itself at identity: every bone's
   * `matrixWorld` at this moment IS the bind pose, and `skeleton()` inverts the same
   * matrices, so the two cannot disagree.
   */
  build(): THREE.BufferGeometry {
    let vc = 0;
    let ic = 0;
    for (const p of this.parts) {
      vc += p.geo.attributes.position.count;
      ic += p.geo.index ? p.geo.index.count : p.geo.attributes.position.count;
    }
    const pos = new Float32Array(vc * 3);
    const nrm = new Float32Array(vc * 3);
    const col = new Float32Array(vc * 3);
    const aoc = new Float32Array(vc);
    const srf = new Float32Array(vc * 2);
    const si = new Uint16Array(vc * 4);
    const sw = new Float32Array(vc * 4);
    const idx = vc > 65000 ? new Uint32Array(ic) : new Uint16Array(ic);
    let vo = 0;
    let io = 0;

    for (const part of this.parts) {
      const bm = this.bones[part.bone].matrixWorld;
      _nm.getNormalMatrix(bm);
      const g = part.geo;
      const p = g.attributes.position;
      const n = g.attributes.normal;
      const c = g.attributes.color;
      const a = g.attributes.aOcc;
      const s = g.attributes.aSurf;
      const r = part.ramp;
      const rl = r ? 1 / Math.max(r.d1 - r.d0, 1e-5) : 0;
      const cnt = p.count;
      for (let i = 0; i < cnt; i++) {
        const o = (vo + i) * 3;
        _pa.set(p.getX(i), p.getY(i), p.getZ(i));
        // Weights come off the LOCAL position, before the bind matrix is applied —
        // the ramp is authored in the same frame as the primitives it splits.
        let w0 = 1;
        if (r) {
          const proj = (_pa.x - r.ox) * r.dx + (_pa.y - r.oy) * r.dy + (_pa.z - r.oz) * r.dz;
          w0 = clamp01((proj - r.d0) * rl);
          // Smoothstep: a linear weight ramp puts a visible crease at both ends of the
          // blend, because the surface derivative jumps there.
          w0 = w0 * w0 * (3 - 2 * w0);
        }
        _pa.applyMatrix4(bm);
        _pb.set(n.getX(i), n.getY(i), n.getZ(i)).applyMatrix3(_nm).normalize();
        pos[o] = _pa.x; pos[o + 1] = _pa.y; pos[o + 2] = _pa.z;
        nrm[o] = _pb.x; nrm[o + 1] = _pb.y; nrm[o + 2] = _pb.z;
        col[o] = c.getX(i); col[o + 1] = c.getY(i); col[o + 2] = c.getZ(i);
        aoc[vo + i] = a.getX(i);
        srf[(vo + i) * 2] = s.getX(i);
        srf[(vo + i) * 2 + 1] = s.getY(i);
        const q = (vo + i) * 4;
        si[q] = part.bone;
        si[q + 1] = r ? r.bone : 0;
        sw[q] = w0;
        sw[q + 1] = 1 - w0;
      }
      if (g.index) {
        for (let i = 0; i < g.index.count; i++) idx[io + i] = vo + g.index.getX(i);
        io += g.index.count;
      } else {
        for (let i = 0; i < cnt; i++) idx[io + i] = vo + i;
        io += cnt;
      }
      vo += cnt;
      g.dispose();
    }
    this.parts.length = 0;

    const out = new THREE.BufferGeometry();
    out.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    out.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
    out.setAttribute('color', new THREE.BufferAttribute(col, 3));
    out.setAttribute('aOcc', new THREE.BufferAttribute(aoc, 1));
    out.setAttribute('aSurf', new THREE.BufferAttribute(srf, 2));
    out.setAttribute('skinIndex', new THREE.BufferAttribute(si, 4));
    out.setAttribute('skinWeight', new THREE.BufferAttribute(sw, 4));
    out.setIndex(new THREE.BufferAttribute(idx, 1));
    out.computeBoundingSphere();
    return out;
  }

  /** Bind-pose inverses come out of the same matrices `build()` baked in. */
  skeleton(): THREE.Skeleton {
    return new THREE.Skeleton(this.bones);
  }
}

// ---------------------------------------------------------------------------------
// The one material the whole kit shares.
// ---------------------------------------------------------------------------------

/**
 * Bike and rider, one `MeshStandardMaterial`, patched with the shared shading library.
 *
 * Three things ride on the vertices that used to force separate materials or separate
 * shading passes:
 *
 *   `color`  albedo, and ONLY albedo now — the baked creases have moved out of it;
 *   `aOcc`   the bake, routed to `gRlfOcc`, which scales irradiance rather than paint.
 *            That is the whole difference between a crease that survives on the shaded
 *            back of a rider and one that does not: the back is lit by ambient, and
 *            multiplying albedo cannot take ambient away;
 *   `aSurf`  roughness (x) and micro-relief weight (y). Roughness in a vertex is what
 *            lets a 0.88 tyre, a 0.26 rim and a 0.72 jersey share one program.
 *
 * `aSurf.y` drives `rlfBumpH` off a small object-space field, so woven surfaces get a
 * grain the sun can actually find. It is object-space, not world-space, or it would
 * crawl across the rider as they move down the mountain; and it is triplanar-blended
 * rather than axis-picked, because a hard axis switch puts a bright seam wherever the
 * dominant normal flips.
 */
export function createKitMaterial(): THREE.MeshStandardMaterial {
  installReliefSystem();
  const mat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.72,
    // One value for the whole kit. With no environment map in this build the difference
    // between the old 0.02 and 0.16 was a few percent of diffuse and nothing else, and
    // it is not worth a second program.
    metalness: 0.08,
  });
  patchStandardMaterial(mat, {
    cacheKey: 'grip-kit-v1',
    vertexCommon: /* glsl */ `
attribute float aOcc;
attribute vec2 aSurf;
varying float vKitOcc;
varying vec2 vKitSurf;
varying vec3 vKitObj;
varying vec3 vKitObjN;
`,
    vertexBody: /* glsl */ `
vKitOcc = aOcc;
vKitSurf = aSurf;
vKitObj = position;
vKitObjN = normal;
`,
    fragmentCommon: /* glsl */ `
varying float vKitOcc;
varying vec2 vKitSurf;
varying vec3 vKitObj;
varying vec3 vKitObjN;
// ~4 cm features: coarse enough to survive the chase camera's ~4 mm/px footprint and
// fine enough that it never competes with an authored panel.
const float KIT_GRAIN_FREQ = 24.0;
const float KIT_GRAIN_M = 0.0042;
`,
    shade: /* glsl */ `{
  gRlfRough = clamp( vKitSurf.x, 0.05, 1.0 );
  gRlfOcc = clamp( vKitOcc, 0.0, 1.0 );
  float kitW = vKitSurf.y;
  if ( kitW > 0.004 ) {
    vec3 kitN = normalize( vRlfWNrm );
    vec3 an = abs( normalize( vKitObjN ) );
    vec3 w3 = an * an;
    w3 /= max( w3.x + w3.y + w3.z, 1e-4 );
    float h = ( rlfFbm( vKitObj.zy * KIT_GRAIN_FREQ, 2 ) * w3.x
              + rlfFbm( vKitObj.xz * KIT_GRAIN_FREQ, 2 ) * w3.y
              + rlfFbm( vKitObj.xy * KIT_GRAIN_FREQ, 2 ) * w3.z - 0.5 );
    RlfLod kitL = rlfLodAt( vRlfWPos );
    gRlfNormalW = rlfBumpH( kitN, vRlfWPos, h * KIT_GRAIN_M * kitW,
                            rlfReliefAmp( kitL, KIT_GRAIN_FREQ ) );
    // A weave scatters as well as it shades: crests catch more than valleys.
    gRlfRough = clamp( gRlfRough + h * 0.12 * kitW, 0.05, 1.0 );
  }
}`,
  });
  return mat;
}

// ---------------------------------------------------------------------------------
// Proportions. A 1.78 m rider, taken off standard anthropometry rather than tuned by
// eye: get these wrong and no amount of shading rescues it, because the eye reads
// proportion before it reads anything else. `grounded` in docs/BAR.md is exactly this.
// ---------------------------------------------------------------------------------

export const RiderSize = {
  torso: 0.50,        // hip centre to shoulder centre
  shoulderHalf: 0.176,
  hipHalf: 0.100,
  upperArm: 0.295,
  foreArm: 0.275,
  thigh: 0.435,
  shin: 0.415,
  neck: 0.215,        // shoulder centre to helmet centre
  helmet: 0.116,
} as const;

const ARM_MAX = RiderSize.upperArm + RiderSize.foreArm;

/**
 * Palette — **two flat high-contrast areas, and everything else is an edge inside one of
 * them.** This is a wave-4 rewrite of a value ladder that had nineteen rungs in it, and
 * the reason for the rewrite is in `assets/reference/target/`.
 *
 * ### What the reference actually is
 *
 * `mavrix-chase-singletrack-ramp.jpeg`, `mavrix-chase-openslope-hud.jpeg` and
 * `mavrix-press-03.jpeg` are the only images we have from the angle the game is played
 * from. All three show the same thing and it is much simpler than what was built here:
 *
 *   **a pale jersey, near-white, as one large uninterrupted mass; dark trousers, near
 *   navy, as a second; a hard edge between them at the waist; and a DARK helmet.**
 *
 * No fabric detail, no logos, no third field. The bike is dark and thin and the rider's
 * light torso is the only bright thing in the frame.
 *
 * ### Why the old ladder failed, measured
 *
 * The wave-3 build's rendered chase frame (`captures/w4-riderview-base/chase.png`) has
 * the jersey at albedo 145, the trousers at 118, the base-layer sleeves at 109 and the
 * pack at 147 — a 38-luma total spread across four of the five largest masses on the
 * body, on a surface lit by ambient alone, where the render is roughly 0.26 x albedo.
 * That is a **10-luma spread in the frame the player sees**, so the whole rider from the
 * armpit to the ankle came out as one khaki field. The carried gap said exactly that:
 * "the seat, both thighs and the pack all sit in the same warm-graphite band."
 *
 * More rungs could not have fixed it. The spread itself was the defect.
 *
 * ### The ladder now, and it is deliberately short
 *
 * ```
 *  199  jersey        pale cool       THE light mass: torso, shoulder caps, sleeve caps
 *  171  jerseyDark    pale shadow     its hems, neckline and raglan seam — EDGES, inside
 *                                     the light mass, so only a 28-step down
 *  159  pad           cool grey       the elbow cup
 *  144  helmetTrim    cool grey       the rear exhaust spine. The LIGHTEST thing on the
 *                                     head, and it is on the arc the chase camera sees
 *  137  baseHi        cool grey       base-layer collar and wrist ribs
 *  124  goggleRim     cool grey       the goggle's two frame rails — an edge, not a face
 *  123  accent        orange          the one saturated note: a badge on the goggle strap,
 *                                     the pack straps, the cuffs
 *  114  trouserHem    cool grey       every welt and seam on the dark field
 *  112  base          cool mid        the base-layer sleeves: BETWEEN the two fields
 *  106  helmet        dark cool       the head reads DARK against sky, as in the reference
 *   90  trouserLo     cool graphite   the trouser below the knee
 *   85  trouser       cool graphite   THE dark mass: thigh, and the field of the garment
 *   77  helmetLow     dark            the occipital pad and the visor's leading edge
 *   71  trouserKnee   dark            knee reinforcement and ankle cuff
 *   63  packLid       dark            the pack lid
 *   61  trouserIn     near-navy       the inseam gusset and the seat — the INNER faces
 *   50  packBody      dark
 *   46  strap         near-black      the goggle strap webbing
 *   40  glove / 40 packPanel / 32 packWebbing
 *   35  goggle        near-black      the LENS. It is the darkest large area on the rider,
 *                                     and that is most of why the head reads as a full
 *                                     face rather than as a head
 *   31  trim
 * ```
 *
 * Jersey to trouser is **114 luma**, against 27 before.
 *
 * The first build of this put the trouser at 69 and the gusset at 48. Measured on the
 * dead-astern beauty frame, the whole garment then rendered between 0 and 10 luma — a
 * black hole with no internal form at all, which is its own failure and which would have
 * disappeared entirely the moment another piece dropped the ambient. Lifting the field to
 * 85 costs 16 luma of contrast against the jersey and buys the leg its own modelling back.
 *
 * ### The three rules this keeps
 *
 * 1. **The surface the chase camera looks at is in shadow**, and shadow compresses
 *    everything toward black at roughly 0.26 x albedo. Any step that has to survive there
 *    must be four times bigger than it would need to be in a swatch. That is why the two
 *    fields are at the extremes of the range rather than politely spaced.
 * 2. **A value break still has to be a form break.** Every entry above that is not one of
 *    the two fields is an *edge* — a welt, a rib, a hem, a cuff — sitting on real
 *    geometry with an occlusion crease under it. None of them is a painted patch.
 * 3. **Non-adjacent masses may share a value.** The pack and the trouser are both dark;
 *    they never touch, because the whole light jersey is between them. Adjacency is what
 *    the ~20-luma rule was ever about.
 *
 * ### The two reversals, stated so nobody quietly restores them
 *
 * - **The helmet is dark now, not bone.** This file used to call it "the brightest thing
 *   on the rider". All three reference frames have a dark helmet over a light jersey, and
 *   `docs/BAR.md` says the reference wins on what a rider actually looks like. A dark head
 *   against sky is also a stronger silhouette than a bright ball against sky, which is
 *   what the chase camera mostly frames it against.
 * - **The pack is dark and small now, not light and large.** Wave 3 made it stone-coloured
 *   and 180 mm wide to stop it fusing with a dark seat. Measured from dead astern it then
 *   covered the entire jersey, so the light mass the reference is built around did not
 *   exist — the back read as pack. It is 128 mm wide and dark now: a hard dark shape on a
 *   large light field, which is what `mavrix-press-03.jpeg` shows.
 *
 * ### Round 3: the head and the pack are no longer allowed to share a value
 *
 * The two reversals above were both right and together they created the round-3 defect:
 * a dark head directly above a dark pack, 1.5 luma apart, with nothing lit between them.
 * Rule 3 says non-adjacent masses may share a value — and the whole light jersey IS
 * between them in space. It is not between them **on screen**, because the chase camera
 * looks down and a pack standing 88 mm off the spine projects up into the base of the
 * helmet. Adjacency is a property of the image, not of the model, and this file had been
 * checking it in the wrong space.
 *
 * So: helmet 78 -> 106, packLid 77 -> 63, the pack down 45 mm, and a jersey-valued collar
 * standing at the neck. 43 luma of separation and a lit plane in the gap.
 *
 * `skin` is kept and used by nothing: there is no bare skin on this rider.
 */
const K = {
  /**
   * Dark full-face shell. See "the two reversals" above — the head is DARK and stays dark.
   *
   * **106 luma, in three steps: 66 -> 78 -> 106, and each step was forced by a
   * measurement.** At 66 the head rendered p50 5 against a kit at 76. At 78 it rendered
   * p50 12 against a torso at 53 — still a fifth of the body it is attached to, and the
   * round-2 critique's whole point was that a shell rendering at 12 cannot show a 58 mm
   * eyeport recess or a 70 mm chin projection no matter how truthfully they are modelled.
   *
   * 106 is deliberately still the darkest large field on the rider — 93 luma under the
   * jersey, 21 under the trouser — so BAR's "dark head against sky" read is unchanged;
   * what moves is that the shell now has somewhere to put a shadow. Against the pack,
   * which the chase camera sees directly under it, the shell was 1.5 luma clear of
   * `packLid` and fused with it into one silhouette; it is 43 clear now, and the pack
   * moved down and darkened as the other half of that fix.
   */
  helmet: 0x646b78,
  helmetLow: 0x474d58,
  /**
   * The shell's opening trim and the rear vent lip — a LIT edge inside the dark head, and
   * the wave-4 round-3 fix.
   *
   * The round-2 head measured p50 12 / p95 47 against a same-frame torso at p50 53, and
   * the critique's conclusion was exactly right: "the 58 mm eyeport recess, 70 mm chin
   * projection and 58-vs-78 occipital step are true of the mesh and invisible in the
   * image." Everything the rebuild put on the head was a step DOWN inside a mass that was
   * already rendering at a tenth of the torso, so none of it had any light to lose.
   *
   * The reference helmet spans 4-102 internally. Ours spanned 1-47. What was missing was
   * the top of the range, and the only thing that can supply it on a surface lit by
   * ambient alone is a lighter albedo on a real edge. 144 is that: 38 luma over the shell,
   * and it appears exactly three times — the vent lip, the occiput scallop and the shell's
   * lower opening — all of them on the REAR, which is the only part of the head the chase
   * camera ever sees.
   */
  helmetTrim: 0x8b9299,
  trim: 0x1b1f25,
  accent: 0xd06a2e,
  /** THE light mass. Not white — a pale cool grey, so the sunlit side has somewhere to go
   *  before it clips, and so it stays a garment rather than a highlight. */
  jersey: 0xc2c8c8,
  /**
   * Its own hems, neckline, raglan seam and sleeve hem: EDGES INSIDE the light field, so
   * a step down rather than a different colour — and only a 26-luma step, not the 39 it
   * was. At 39, with the armpit crease at 0.52 on top of it, the deltoid caps read as two
   * light lozenges flanking a light torso in the `manual` capture: the wave-3 "assembled
   * balls" note reappearing in the new palette. The reference torso is one uninterrupted
   * mass and its seams are barely visible; the hem at the waist does not need this step
   * to work, because the `trim` band under it is 143 luma darker again.
   */
  jerseyDark: 0xa5adae,
  /**
   * The pack, one full step darker than it was, and the reason is the head above it.
   *
   * `helmet` 78.4 and `packLid` 76.5 sat 1.5 luma apart with nothing lit between them, and
   * from the chase camera — which looks DOWN, so the pack projects up toward the helmet
   * and the 185 mm of jersey between them collapses to nothing — the two fused into a
   * single dark column. The critique called it a brimmed hat with a dark bib and that is
   * precisely what the delivered frame shows.
   *
   * Value separation is half the fix (43 luma now), the pack dropping 45 mm is the other
   * half, and the lit collar built at the neck is what fills the space that opens up.
   */
  packBody: 0x2f333a,
  packLid: 0x3b4048,
  packPanel: 0x25292f,
  packWebbing: 0x1c2025,
  packDark: 0x131518,
  /**
   * THE dark mass: the field of the trouser, from the waistband to the knee.
   *
   * Cool graphite rather than the warm khaki it was. Warm mid-value against warm mid-value
   * dirt is the one pairing the ground can absorb, and three waves of critics have said
   * the rider does not read against it.
   */
  trouser: 0x4f5560,
  /**
   * The inseam gusset and the seat — **the inner faces of both legs and the surface
   * between them**. 21 luma under the field, and it is the value half of the carried gap:
   * the two surfaces that face each other across the crotch are the darkest thing on the
   * lower body, so the notch between the legs reads as a notch even before the geometry
   * behind it resolves.
   */
  trouserIn: 0x383d46,
  /** Every welt and seam on the dark field: a LIT edge inside a dark mass, which is the
   *  only way a seam reads once the whole garment is in shadow. */
  trouserHem: 0x6d737c,
  pad: 0x9aa0a6,
  padDark: 0x646b73,
  skin: 0xa87952,
  glove: 0x24282e,
  /**
   * **The goggle lens, and it is DARK.** It used to be `0x8fa8b2` — a pale blue-grey, the
   * lightest thing on the head — carried on a box 0.170 m wide that was wider than the
   * shell it sat in, so its two ends came out through the temples. Measured on the wave-4
   * round-1 build (`captures/w4-headview-base/head.json`) that put two clusters of pixels
   * over luma 190 side by side at eye height, 854 px and 396 px, peaking at 237. The
   * round-1 critique read them exactly as what they look like: eyes.
   *
   * A goggle lens seen from outside is a mirror-tinted dark object. One dark band across
   * the whole eyeport is a goggle; two bright spots at eye height is a face, at any
   * distance, whatever they are actually made of.
   */
  goggle: 0x1e232b,
  /** The goggle's frame rails, above and below the lens. Two thin LIT edges bracketing
   *  the darkest band on the head — the same welt logic as `trouserHem`, on the one
   *  feature that has to say "goggles" from 40 px. */
  goggleRim: 0x767d86,
  /** Goggle strap and helmet webbing. Near-black: a strap is a strap, not a headline. */
  strap: 0x2a2e35,
  /**
   * The second garment layer — BAR's "long-sleeve base layer" — and the reason there is
   * no bare skin on this rider.
   *
   * It sits deliberately BETWEEN the two fields, at 112. Both neighbours were measured
   * rather than chosen: at 83 it rendered 21.5 against the glove's 17.9 and the forearm
   * fused into a black stick (wave 3), and at the trouser's 69 it would fuse with the
   * lower body instead. 112 clears the glove by 27 rendered luma and the trouser by 11,
   * and the arm then reads light cap -> mid sleeve -> dark glove, three steps down a limb
   * that used to be one cone.
   */
  base: 0x6b7178,
  /** Its collar and wrist ribs. A rib is an EDGE, so it is only one step up. */
  baseHi: 0x848a90,
  /**
   * The trouser below the knee. A hair LIGHTER than the thigh (91 vs 85), not darker:
   * the lower leg is the part of the rider that moves most against the ground, and a
   * garment that gets darker toward the ground puts the moving part in the same value
   * band as the shadow it is moving through.
   *
   * It was a full step lighter (99 against 85) and in the `parked` and `stoppie` captures
   * that read as a grey boot pulled on over a dark trouser — a two-garment leg, which is
   * the opposite of the reference's one uniform dark field from waist to ankle. What
   * separates knee from shin is the reinforcement panel and the seams, not the field.
   */
  trouserLo: 0x555b65,
  /**
   * The knee reinforcement, and the cuff. One panel value used twice, at the two places
   * the trouser has to say "there is armour under here" and "this is where it ends" —
   * a 13-step under the thigh and a 23-step under the shin, so the leg still reads
   * thigh / knee / shin / cuff at any distance where the leg is more than a few pixels.
   */
  trouserKnee: 0x42474f,
} as const;

/** Roughness/relief pairs, so the same surface is the same surface everywhere. */
const SURF = {
  fabric: [0.80, 1.0],
  fabricTight: [0.72, 0.7],
  armour: [0.48, 0.35],
  skin: [0.66, 0.18],
  /**
   * The helmet shell, and it is used NOWHERE else — the whole bike is built in
   * `bikeModel.ts` with its own numbers, so this is safe to move for the head alone.
   *
   * It was 0.34. With no environment map in this build the only specular the shell can
   * ever have is the sun, and a 0.34 lobe on a 0.23 m sphere puts two small, near-white,
   * *round* highlights on it — measured at 236 and 252 luma over 11.9% of the head's
   * pixels in the profile view of the round-1 build. Round highlights in pairs on a head
   * are read as eyes before they are read as anything else, and that is half of the
   * round-1 gap. 0.55 keeps the shell obviously harder than the jersey next to it and
   * spreads the same energy over a broad sheen the eye reads as gloss rather than as a
   * feature.
   */
  shell: [0.55, 0.06],
  /** The goggle lens. Tighter than the shell, because a lens SHOULD have a highlight —
   *  but it is one wide band across the face, so what it gets is a streak. */
  lens: [0.40, 0.0],
  rubber: [0.88, 0.5],
  webbing: [0.86, 0.8],
} as const;

// ---------------------------------------------------------------------------------
// Pose constants. Local to this module by design — tuning.ts belongs to the physics
// and control owners.
// ---------------------------------------------------------------------------------

const POSE = {
  /** Neutral hip position over the bottom bracket, m. A 1.78 m rider standing on
   * pedals 0.34 m up has their hip joint about 1.12 m off the ground with a working
   * amount of knee bend; drop it much below that and the rider is in a deep squat in
   * every frame, which is the single most common way a bike game gets this wrong. */
  hipX: -0.055,
  hipY: 1.10,
  /**
   * Half the distance between the two thigh roots, m.
   *
   * It was `RiderSize.hipHalf * 0.9` = 0.090, which put the two legs 0.180 m apart while
   * each trouser leg was 0.172 m wide — so the two lofts *intersected* on the centreline
   * and the carried wave-3 gap ("no background gap between the legs") was a geometric
   * certainty before anything was shaded. 0.098 is the femoral head separation on a
   * 1.78 m rider plus the soft tissue outboard of it, and paired with the trouser section
   * being drawn in laterally it opens roughly 80 mm of clear air between the legs.
   *
   * It is a POSE constant and not a RiderSize one because it is where the rider *stands*,
   * not how wide their pelvis is, and it has to be identical in `bindPose` and `update`
   * or the skin weights are authored against a pose the rig never reaches.
   */
  hipSplay: 0.098,
  /** How much of BikeState.riderFore the hips actually travel. */
  foreGain: 1.0,
  /** A rider getting behind the saddle also gets low — that is what keeps the pedals
   * inside leg reach, and it is also what the move actually looks like. */
  backDrop: 0.45,
  /** Fraction of bike lean the rider gives back on flat ground. Zero in a berm. */
  counterLean: 0.46,
  /** Fraction of that same roll the shoulders take. The spine bends: hips go a long way
   * to the outside, the head barely moves, and the arms absorb the difference. Rolling
   * the whole rider rigidly puts the outside grip out of arm's reach at 40 degrees. */
  shoulderRoll: 0.30,
  /** Where the shoulders want to sit relative to the middle of the bars. */
  reachX: -0.155,
  reachY: 0.405,
  /** Extra shoulder set-back per metre of rearward weight shift: the arms straighten as
   * the rider gets behind the saddle instead of the torso folding in half. */
  reachBack: 0.50,
  /**
   * Speed band the tuck comes in over, m/s — 20 km/h to 60 km/h exactly, because that
   * is the band a viewer can put a number to.
   */
  tuckLo: 5.55,
  tuckHi: 16.7,
  /**
   * How far the tuck actually moves things, m.
   *
   * The ceiling on all of this is arm reach, and it is a hard one. The bars sit 1.03 m
   * up and the shoulders 0.40 m above them; drop the chest straight onto the bars the
   * way a road tuck does and the shoulder-to-grip distance falls to 0.22 m against a
   * 0.57 m arm, which crumples the elbows into the chest. A DH rider does not do that
   * either — the move is *hips back and down* with the chest following, which flattens
   * the back while keeping the arms long. So the hips travel further than the shoulders
   * and most of the hip travel is rearward.
   */
  tuckHip: 0.128,
  tuckHipBack: 0.095,
  tuckReach: 0.128,
  tuckHead: 0.050,
} as const;

/**
 * Limb compliance — the rider's mass on four springs.
 *
 * Gains are per g of *excess* load, split because a limb is not symmetric: it can fold a
 * long way under load and can only ever straighten to its own length, so compression and
 * extension want different rates and different stops.
 *
 * Legs move about twice what arms do, which is right — the rider is standing on the
 * pedals, so the legs carry the mass and the arms mostly steer and brace.
 */
const LIMB = {
  /** Hip drop per g of load above static, and the stop, m. */
  legComp: 0.082,
  legCompMax: 0.150,
  /** Hip rise as the legs unload, and the stop. Full extension is reached in free fall. */
  legExt: 0.104,
  legExtMax: 0.104,
  armComp: 0.050,
  armCompMax: 0.090,
  armExt: 0.058,
  armExtMax: 0.058,
  /** Left/right split per g of lateral acceleration, m. */
  legLat: 0.040,
  armLat: 0.028,
  /** Spring: ~1.85 Hz, slightly under-damped so a landing settles with one visible
   * rebound rather than creeping back like a hydraulic. */
  omega: 11.6,
  zeta: 0.62,
  /** Integrator substep, s. The render dt can be 100 ms after a stall and an explicit
   * 11.6 rad/s spring at that step is unstable, so it is subdivided. */
  sub: 1 / 200,
} as const;

/**
 * THE FALL. Wave-5 coherence gap, and it is the second job on this piece:
 *
 * > "The failure state is unauthored and the player sees it six times a run. On a crash
 * >  the rider topples as a rigid mannequin with its arms still out to bars that are not
 * >  there, and the bike is completely gone (submerged). Four systems read `s.crashed` —
 * >  physics, camera, audio, vfx — and rider.ts and bikeModel.ts do not."
 *
 * `s.crashed` is a real 2.4 s state (`Tuning.impact.crashRecoverTime`): the sim drags the
 * bike to a stop, the balance assist is switched off, and lean and pitch run free until
 * the remount. For every one of those 2.4 s this rig was solving the same constraint
 * chain it solves while riding — hands welded to the grips, feet welded to the pedals,
 * shoulders anchored 405 mm above the bar centre — because none of it reads the flag.
 * The result is not a bug in the usual sense; it is simply an animation nobody wrote.
 *
 * What is written here is one pose and one blend, not a ragdoll:
 *
 * - **The hands let go.** They leave the grips and come up in front of the face, elbows
 *   in. That is what a rider does going down and it is the only thing that can be true
 *   once the bars are lying on the hillside.
 * - **The feet come off the pedals** and the knees draw up. A crashed rider is not
 *   standing on anything.
 * - **The back rounds and the chin drops.** The shoulder target stops being a function of
 *   where the bars are — which is the single change that stops the mannequin read,
 *   because the straight back was a *consequence* of reaching for a cockpit.
 * - **The counter-lean goes to zero.** Counter-lean is an act of balance; nobody down on
 *   the dirt is balancing.
 *
 * Rates are asymmetric on purpose. Going down is fast (a rider lets go in about a tenth
 * of a second) and getting back on is slower, so the remount reads as picking the bike up
 * rather than as a snap.
 */
const CRASH = {
  /** Blend rate into the fall, 1/s. */
  inRate: 13,
  /** ...and back out of it. */
  outRate: 3.4,
  /** Hands: up, forward of the chest and out, in torso frame. m. */
  handUp: 0.05,
  handFwd: 0.20,
  handOut: 0.115,
  /** Feet: knees drawn up under the rider. m, in torso frame from the hip. */
  footFwd: 0.17,
  footDown: 0.29,
  footOut: 0.185,
  /** Shoulders: how much of the torso length is left once the back rounds. */
  torsoFold: 0.86,
  /** ...and how far forward the rounded shoulders go. m. */
  shoFwd: 0.15,
  /** The chin comes down. rad, added to the look pitch. */
  lookDown: 0.85,
  /** Hips settle onto the bike as the legs stop carrying anything. m. */
  hipDrop: 0.085,
  hipBack: 0.075,
} as const;

const G = 9.81;
const MG = Tuning.bike.mass * G;
/** Static axle loads, N — the datum each limb spring is measured against. */
const STATIC_FRONT = MG * (1 - Tuning.bike.rearWeightBias);

// ---------------------------------------------------------------------------------
// Two-bone IK.
// ---------------------------------------------------------------------------------

const _ik0 = new THREE.Vector3();
const _ik1 = new THREE.Vector3();

/**
 * Place the middle joint of a two-bone chain. `pole` is the direction the joint should
 * bend toward — an elbow points back and out, a knee points forward and out — and only
 * its component perpendicular to the root->target line is used, so it can be a rough
 * hint rather than an exact plane normal.
 *
 * The target is clamped into reach rather than the chain being allowed to stretch. A
 * stretched limb is invisible from most angles and then suddenly is not; a limb that is
 * straight and two centimetres short reads as a rider at full extension, which is what
 * is actually happening.
 */
function solveTwoBone(
  root: THREE.Vector3, target: THREE.Vector3,
  l1: number, l2: number, pole: THREE.Vector3,
  outJoint: THREE.Vector3, outTip: THREE.Vector3,
): void {
  _ik0.subVectors(target, root);
  let d = _ik0.length();
  if (d < 1e-5) { _ik0.set(0, -1, 0); d = 1e-5; } else { _ik0.divideScalar(d); }
  const dc = clamp(d, Math.abs(l1 - l2) + 1e-3, l1 + l2 - 1e-3);
  outTip.copy(root).addScaledVector(_ik0, dc);

  const cosA = clamp((l1 * l1 + dc * dc - l2 * l2) / (2 * l1 * dc), -1, 1);
  const a = Math.acos(cosA);

  _ik1.copy(pole).addScaledVector(_ik0, -pole.dot(_ik0));
  if (_ik1.lengthSq() < 1e-8) {
    _ik1.set(0, 0, 1).addScaledVector(_ik0, -_ik0.z);
    if (_ik1.lengthSq() < 1e-8) _ik1.set(1, 0, 0);
  }
  _ik1.normalize();
  outJoint.copy(root)
    .addScaledVector(_ik0, Math.cos(a) * l1)
    .addScaledVector(_ik1, Math.sin(a) * l1);
}

const _bnX = new THREE.Vector3();
const _bnY = new THREE.Vector3();
const _bnZ = new THREE.Vector3();

/**
 * A bone authored from its own origin down -Y, aimed between two points.
 *
 * There is no mesh here any more — the geometry it carries went into the shared skinned
 * buffer at construction and this only moves the transform the buffer is bound to.
 */
class Limb {
  readonly bone = new THREE.Bone();
  /**
   * Point the bone's -Y at `to`, and roll it so its +X gets as close to `ref` as it
   * can. The roll matters: knee pads, shin guards and elbow cups are authored on +X,
   * and a bone aimed with a free roll puts them somewhere different every frame.
   */
  aim(from: THREE.Vector3, to: THREE.Vector3, ref: THREE.Vector3): void {
    this.bone.position.copy(from);
    _bnY.subVectors(from, to);
    if (_bnY.lengthSq() < 1e-8) return;
    _bnY.normalize();
    _bnX.copy(ref).addScaledVector(_bnY, -ref.dot(_bnY));
    if (_bnX.lengthSq() < 1e-6) {
      _bnX.set(0, 0, 1).addScaledVector(_bnY, -_bnY.z);
      if (_bnX.lengthSq() < 1e-6) _bnX.set(1, 0, 0);
    }
    _bnX.normalize();
    _bnZ.crossVectors(_bnX, _bnY);
    _m.makeBasis(_bnX, _bnY, _bnZ);
    this.bone.quaternion.setFromRotationMatrix(_m);
  }
}

// ---------------------------------------------------------------------------------
// Rider rig.
// ---------------------------------------------------------------------------------

/** Where the bike's contact points ended up this frame, in chassis space. */
export interface Cockpit {
  gripL: THREE.Vector3;
  gripR: THREE.Vector3;
  pedalL: THREE.Vector3;
  pedalR: THREE.Vector3;
  /** Bottom bracket height, m — the pivot the body rolls about. */
  bbY: number;
  /** Saddle, so the rider can get low without going through it. */
  saddleX: number;
  saddleTop: number;
}

export class RiderRig {
  /** Parent this into the leaned/pitched chassis frame with an identity transform. */
  readonly root = new THREE.Group();

  private readonly torso = new THREE.Bone();
  private readonly head = new THREE.Bone();
  private readonly upperArm: Limb[] = [];
  private readonly foreArm: Limb[] = [];
  private readonly thigh: Limb[] = [];
  private readonly shin: Limb[] = [];

  // Scratch. This runs every frame and allocates nothing.
  private readonly hip = new THREE.Vector3();
  private readonly sho = new THREE.Vector3();
  private readonly shoTarget = new THREE.Vector3();
  private readonly bodyUp = new THREE.Vector3();
  private readonly bodyRight = new THREE.Vector3();
  private readonly bodyFwd = new THREE.Vector3();
  private readonly gripMid = new THREE.Vector3();
  private readonly joint = new THREE.Vector3();
  private readonly tip = new THREE.Vector3();
  private readonly pole = new THREE.Vector3();
  private readonly limbRoot = new THREE.Vector3();
  private readonly limbTarget = new THREE.Vector3();
  private readonly fwdRef = new THREE.Vector3(1, 0, 0);
  private readonly tmp = new THREE.Vector3();
  /** Crash targets, resolved per limb per frame. Scratch — allocates nothing. */
  private readonly crashT = new THREE.Vector3();

  private steepSmooth = 0;
  private tuckSmooth = 0;
  private counterSmooth = 0;
  /** 0 riding, 1 down. See `CRASH` and the fall block in `update`. */
  private crashMix = 0;

  // Limb springs: displacement along the limb (positive = folded toward the contact)
  // and its rate. Index 0 is left, 1 is right, so a corner can load one side.
  private readonly armX = new Float32Array(2);
  private readonly armV = new Float32Array(2);
  private readonly legX = new Float32Array(2);
  private readonly legV = new Float32Array(2);
  private readonly armT = new Float32Array(2);
  private readonly legT = new Float32Array(2);
  private armDrive = 0;
  private legDrive = 0;
  /** Averages and per-side residuals, resolved once per frame. */
  private armAvg = 0;
  private legAvg = 0;

  /**
   * `rest` is the cockpit as the bike is drawn standing still. It is used for exactly
   * one thing and it matters: the bind pose. Linear blend skinning distorts in
   * proportion to how far a bone has rotated from the pose its weights were authored
   * in, so binding the rider standing to attention would put a candy-wrapper pinch in
   * the one place this whole exercise exists to fix — the shoulder. Binding in the
   * riding position instead means the blend zones sit at zero relative rotation for most
   * of the time the camera is looking at them.
   */
  constructor(skin: Skin, rest: Cockpit) {
    const S = RiderSize;

    this.torso.name = 'torso';
    this.head.name = 'head';
    this.root.add(this.torso, this.head);
    for (let i = 0; i < 2; i++) {
      const side = i === 0 ? 'L' : 'R';
      const ua = new Limb(); ua.bone.name = 'upperArm' + side;
      const fa = new Limb(); fa.bone.name = 'foreArm' + side;
      const th = new Limb(); th.bone.name = 'thigh' + side;
      const sn = new Limb(); sn.bone.name = 'shin' + side;
      this.upperArm.push(ua);
      this.foreArm.push(fa);
      this.thigh.push(th);
      this.shin.push(sn);
      this.root.add(ua.bone, fa.bone, th.bone, sn.bone);
    }

    // Pose everything into the bind position before a single vertex is authored.
    this.bindPose(rest);

    const iTorso = skin.bone(this.torso);
    const iHead = skin.bone(this.head);
    const iArm: number[] = [];
    const iFore: number[] = [];
    const iThigh: number[] = [];
    const iShin: number[] = [];
    for (let i = 0; i < 2; i++) {
      iArm.push(skin.bone(this.upperArm[i].bone));
      iFore.push(skin.bone(this.foreArm[i].bone));
      iThigh.push(skin.bone(this.thigh[i].bone));
      iShin.push(skin.bone(this.shin[i].bone));
    }

    // --- torso: hips at local 0, shoulders at +torso, +X is the chest ---------------
    {
      const b = new PartBuilder();
      // Everything on the torso that faces down loses a quarter of its value. This alone
      // is most of the difference between "a balloon" and "stacked masses" — the seat,
      // the jersey hem and the underside of the pack are the three surfaces the key
      // light never reaches, and they were previously the same tone as their tops.
      b.underShade(0.26);
      b.surf(SURF.fabric[0], SURF.fabric[1]);

      // --- the trouser, at the hip ---------------------------------------------------
      // WAVE 4. The carried gap is "from the waist to the knee the rider is one smooth
      // inflated volume ... no background gap between the legs, so below the jersey the
      // rider reads as a sack rather than two legs." The hip mass is where that starts.
      //
      // It was 0.296 m across and 0.176 m deep — a balloon wider than the two legs under
      // it, so the legs had nowhere to emerge FROM: they came out of the bottom of a
      // sphere that was already wider than the pair of them, and the eye read one object.
      // Measured against the reference (`mavrix-chase-singletrack-ramp.jpeg`, dead astern)
      // the rider's hips are about 0.63 of their shoulder width; ours were 0.78, and the
      // shoulders here are 0.352 m over the joints. 0.236 m is that ratio.
      //
      // Narrowing the hip is half of the gap fix and it is the half nothing else can do:
      // the crotch cannot be open if the mass above it is wider than the space the legs
      // are trying to leave between them.
      b.blob(0.004, 0.022, 0, 0.094, 0.086, 0.124, K.trouser);

      // --- the seat: TWO PANELS AND A SEAM, not one egg -----------------------------
      // The wave-3 gap named this exactly: "a hard-edged colour patch painted onto a
      // smooth primitive instead of a form edge". A stripe of darker albedo down the
      // middle of an ellipsoid is a decal. Two panels splayed apart leave a real 25 mm
      // valley between them that catches its own shadow, and a flatlock welt sitting
      // down in that valley gives the eye a lit edge inside a dark one. Garments read as
      // garments because they are made of panels; this is the cheapest true version of
      // that on the one surface the chase camera never stops looking at.
      //
      // The panels overlap heavily on purpose. Splayed far enough apart to read as two
      // separate lobes they become a pair of matched spheres — the one arrangement the
      // eye calls "primitives" however it is lit, and the exact note wave 2 got about
      // the two-cheek version of this. What is wanted is ONE form with a seam in it, so
      // the overlap leaves a 16 mm groove and no more.
      // ONE seat mass, 0.248 m across, standing 14 mm proud of the hip at its deepest.
      //
      // It was two, splayed 72 mm apart, and lit from behind at a trailhead they read as
      // exactly what the wave-2 note warned about and the wave-3 critique then named: a
      // pair of matched spheres, each with its own highlight, which is the one arrangement
      // the eye calls "primitives" however it is shaded. A rider does have two of these,
      // and the way to say so is a *seam* — the welt below, sitting in its own occlusion
      // crease — not two balls. One mass with a line down it reads as a garment; two
      // masses read as two masses, and no palette rescues that.
      //
      // WAVE 4: it is narrower (0.216 m, from 0.248) and it is `trouserIn` — the same
      // value as the two legs' inner faces, because it is the same panel of the same
      // garment and because everything that faces into the crotch has to be the darkest
      // thing on the lower body for the notch between the legs to read.
      b.blob(-0.044, -0.004, 0, 0.062, 0.074, 0.108, K.trouserIn, 16, 0.10);
      // **The crotch bridge is gone, and that is the wave-4 geometry fix.**
      //
      // There used to be a second ellipsoid here, 0.212 m wide, sitting under the seat and
      // spanning from one leg to the other. Its stated job was to stop the two thighs
      // reading as "two tubes that happen to start near each other" — but a solid that
      // spans the crotch is exactly a solid that CLOSES the crotch, and the carried gap is
      // that there is "no background gap between the legs". It was the object filling it.
      //
      // What replaces it is not another solid: it is the hip and seat masses above being
      // narrow enough to close the top of the slot on their own, so the garment still
      // forks from one mass, and then nothing at all below torso y = -0.075 — which is
      // roughly where a crotch is. The two legs run free from there to the ankle with the
      // bike and the trail visible between them.
      b.surf(SURF.fabricTight[0], SURF.fabricTight[1]);
      // Centre-back welt, tracked down the groove floor between the two panels: it never
      // stands proud of the panel crests, so it cannot flash, and it is a value step
      // *up* inside the darkest mass on the rider — a lit edge in a dark field, which is
      // how a seam actually reads.
      const welt: [number, number][] = [
        [-0.079, 0.056], [-0.106, 0.008], [-0.106, -0.032], [-0.078, -0.070],
      ];
      for (let i = 0; i < welt.length - 1; i++) {
        b.seg(welt[i][0], welt[i][1], 0, welt[i + 1][0], welt[i + 1][1], 0,
          0.010, K.trouserHem, 0.010, 8, 2);
      }
      // Yoke seam across the top of the seat, following the hip surface rather than
      // cutting across it — a straight tube here sinks in the middle and floats off the
      // ends, which is worse than no seam at all.
      const yoke: [number, number, number][] = [
        [-0.086, 0.066, 0], [-0.076, 0.066, 0.052], [-0.052, 0.060, 0.090],
        [-0.014, 0.048, 0.114],
      ];
      for (const sz of [1, -1]) {
        for (let i = 0; i < yoke.length - 1; i++) {
          b.seg(yoke[i][0], yoke[i][1], sz * yoke[i][2],
            yoke[i + 1][0], yoke[i + 1][1], sz * yoke[i + 1][2],
            0.0085, K.trouserHem, 0.0085, 6);
        }
      }
      b.surf(SURF.fabric[0], SURF.fabric[1]);
      // Waistband. A light band with the jersey hem overhanging it: the shorts need a
      // top edge or the jersey pours straight into them and the torso is one bag.
      b.blob(0.008, 0.100, 0, 0.096, 0.024, 0.114, K.trouserHem);
      b.blob(0.008, 0.116, 0, 0.093, 0.020, 0.111, K.trim);

      // --- jersey ------------------------------------------------------------------
      b.blob(0.010, 0.192, 0, 0.100, 0.132, 0.126, K.jersey);
      b.blob(0.014, 0.368, 0, 0.115, 0.149, 0.168, K.jersey);
      // Jersey hem, flared proud of the waistband and in the jersey's own shadow value,
      // so there is a lit edge above a dark one right where the two garments meet.
      b.blob(0.010, 0.138, 0, 0.104, 0.034, 0.130, K.jerseyDark);
      b.box(0.020, 0.24, 0.030, 0.107, 0.318, 0, K.jerseyDark, 0.05);
      // --- the neck, and where the two layers are readable at once -------------------
      // BAR's archetype is "a sleeveless or short-sleeve jersey worn over a long-sleeve
      // base layer", and the collar is the one place a viewer can see BOTH garments in
      // the same 60 mm: the jersey's neckline welt, and the base layer's rib standing out
      // of it. It also removes the fifth bare cylinder — a tan neck under a bone helmet
      // was reading as the join between two toys.
      b.surf(SURF.fabricTight[0], SURF.fabricTight[1]);
      b.blob(0.018, S.torso - 0.052, 0, 0.078, 0.024, 0.088, K.jerseyDark, 12, 0.10);
      b.surf(SURF.fabric[0], SURF.fabric[1]);
      b.seg(0.020, S.torso - 0.045, 0, 0.048, S.torso + 0.082, 0, 0.053, K.base, 0.047);
      b.surf(SURF.fabricTight[0], SURF.fabricTight[1]);
      // The base layer's rib. It rides just inside the jersey collar's lip — round 2 moved
      // the collar down 22 mm and left this where it was, which put a second light band
      // 20 mm ABOVE the first one and undid the point of the move.
      b.blob(0.030, S.torso + 0.024, 0, 0.052, 0.024, 0.055, K.baseHi, 10, 0.14);

      // --- THE LIT COLLAR, i.e. a neck ------------------------------------------------
      //
      // The round-3 gap ends with a requirement rather than a diagnosis: "enough value
      // separation or a lit collar between shell and pack that a neck exists." Both are
      // built, because on this rider neither is sufficient alone.
      //
      // Why a neck did not exist: the chase camera looks DOWN, so the pack — 130 mm behind
      // the spine — projects UP toward the helmet and the 185 mm of jersey between the two
      // collapses to nothing on screen. A dark shell landing straight onto a dark pack lid
      // with no lit surface anywhere between them is one silhouette, and one silhouette in
      // that shape is a hat with a bib. It cannot be fixed at the helmet alone: the head is
      // the darkest field on the rider by design and must stay so.
      //
      // What fixes it is a LIT HORIZONTAL PLANE at the top of the neck. This is a raised
      // jersey collar in the light field's own value — 199 against the shell's 106 — and
      // from a camera that looks down it presents its top face, which is the one surface
      // orientation on the whole upper body that catches sky rather than being shaded by
      // the rider's own back. It reads at the ~45 px the head occupies because it is a
      // value block, not a line.
      //
      // **WAVE 5 ROUND 2 DROPS IT 22 mm**, and the reason is the head. This round's brief
      // stated it as a consequence rather than as a task: *"then the head stops needing a
      // bright collar band wedged under it to keep it from fusing with the pack."* It was
      // needed because the shell had no outline of its own. With the peak 47 mm proud, a
      // 36 mm ear pocket and a chin bar that ends in a 120 mm edge instead of a point, the
      // head separates from the pack by SHAPE, and the collar can go back to being a collar.
      //
      // It is not deleted, and it should not be: it is still the one lit horizontal plane
      // between two of the darkest fields on the rider. What it stops being is a 199-luma
      // horseshoe wedged against the chin — measured on the delivered 8x crop of the
      // round-1 build, its top edge was cutting the bottom off the chin bar, which is one
      // of the three features the gap asks to see.
      b.surf(SURF.fabric[0], SURF.fabric[1]);
      b.loft([
        { y: S.torso + 0.044, cx: 0.020, rx: 0.052, rz: 0.058 },
        { y: S.torso + 0.032, cx: 0.018, rx: 0.064, rz: 0.072 },
        { y: S.torso + 0.016, cx: 0.015, rx: 0.065, rz: 0.073 },
        { y: S.torso - 0.004, cx: 0.012, rx: 0.076, rz: 0.086 },
      ], 18, K.jersey);
      // Its own rolled edge, a step down INSIDE the light field, so the collar has an
      // outline against the helmet above it instead of ending where the surface turns.
      b.surf(SURF.fabricTight[0], SURF.fabricTight[1]);
      b.loft([
        { y: S.torso + 0.048, cx: 0.020, rx: 0.047, rz: 0.053 },
        { y: S.torso + 0.044, cx: 0.020, rx: 0.055, rz: 0.061 },
        { y: S.torso + 0.038, cx: 0.019, rx: 0.052, rz: 0.058 },
      ], 18, K.jerseyDark);
      b.surf(SURF.fabric[0], SURF.fabric[1]);

      // --- the pack: a real object, standing off a real gap -------------------------
      // Everything about this is aimed at one sentence in the critique: "the near-black
      // pack ... a flat decal on a teal sphere". A decal has no thickness, no lit face
      // and no shadow of its own. This has all three:
      //   - the body stands 55 mm off the jersey, so it breaks the outline in profile;
      //   - the lid overhangs the body by 8 mm and is a full value step lighter, so from
      //     the chase camera — which looks DOWN at the rider — the first thing the eye
      //     lands on is a lit horizontal plane, not a dark hole;
      //   - the bottom tapers forward and away, leaving a gap under the pack that the
      //     occlusion probes below turn into the hardest edge on the model.
      //
      // ROUND 3 made it a LOFT and 180 mm across, on the reasoning that a smaller bag
      // could not be seen from dead astern behind a 286 mm pair of shoulders.
      //
      // **WAVE 4 reverses the size and the value, and the reason is the reference.** A
      // 180 mm stone-coloured bag standing 110 mm off the back does not "break the
      // outline" from the chase camera — it *becomes* the outline. Measured on the wave-3
      // chase frame, the pack occupies the entire visible width of the rider's back and
      // the jersey survives only as two patches at the shoulders, so the one thing all
      // three reference frames are built around — a single large pale torso — did not
      // exist on this model at all. The carried gap named the consequence: the pack sits
      // "in the same warm-graphite band" as the seat and the thighs, which it did,
      // because it was the same hue at a similar value and it was the size of the back.
      //
      // It is 128 mm across now, it sits high between the shoulder blades, and it is dark
      // (`mavrix-press-03.jpeg` — a compact dark pack on a light back). That is a *harder*
      // silhouette break than the big one was, because a small dark shape on a large light
      // field has an edge everywhere, while a large mass in the field's own value has one
      // nowhere. It keeps every structural property round 3 built it for:
      //   - the lid FLARES over the body and steps back in under it, so the overhang is a
      //     real occluded step rather than two ellipsoids;
      //   - the body swells and TAPERS into the small of the back, so it has a profile;
      //   - it stands 88 mm off the jersey, so it still breaks the outline in three
      //     quarter view and still casts the hardest crease on the model into the slot
      //     behind it.
      //
      // **WAVE 4 ROUND 3 drops it 45 mm.** Value separation from the helmet was half the
      // round-3 fix and this is the other half. The lid's top ring sat at y 0.404 with the
      // helmet's occiput ending at 0.649 — 245 mm of clear jersey between them, and none of
      // it visible, because a camera looking down at a pack that stands 88 mm off the spine
      // projects the lid straight up into the base of the helmet. 45 mm is what it takes to
      // put the collar's lit top face into that gap on screen rather than merely in space.
      b.surf(SURF.fabric[0], SURF.fabric[1]);
      /** Everything on the pack hangs off this, so it moves as one object. */
      const packY = -0.045;
      const packRings: LoftRing[] = [
        { y: packY + 0.404, rx: 0.018, rz: 0.028, cx: -0.120 },
        { y: packY + 0.384, rx: 0.034, rz: 0.046, cx: -0.126 },
        // Lid skirt — the widest ring on the pack, and the overhang.
        { y: packY + 0.362, rx: 0.039, rz: 0.051, cx: -0.130 },
        { y: packY + 0.348, rx: 0.037, rz: 0.049, cx: -0.128 },
        // ...and the step back in underneath it.
        { y: packY + 0.336, rx: 0.033, rz: 0.044, cx: -0.124 },
        { y: packY + 0.306, rx: 0.036, rz: 0.049, cx: -0.124, fold: 0.010, foldN: 4 },
        { y: packY + 0.276, rx: 0.035, rz: 0.047, cx: -0.120, fold: 0.014, foldN: 4 },
        { y: packY + 0.250, rx: 0.028, rz: 0.038, cx: -0.113 },
        { y: packY + 0.230, rx: 0.018, rz: 0.025, cx: -0.106 },
        { y: packY + 0.218, rx: 0.008, rz: 0.011, cx: -0.102 },
      ];
      b.loft(packRings.slice(0, 4), 20, K.packLid, true);
      b.loft(packRings.slice(3), 20, K.packBody);
      const packProud = (k: number, dr: number): LoftRing => ({
        ...packRings[k], rx: packRings[k].rx + dr, rz: packRings[k].rz + dr,
      });
      // Rear pocket panel: a partial loft over the outward-facing third of the body,
      // standing 7 mm proud with its own top and bottom edge. theta = PI is the face
      // pointing away from the rider, which is the face the chase camera sees.
      b.loft(
        [packProud(5, 0.007), packProud(6, 0.007), packProud(7, 0.007)],
        12, K.packPanel, false, Math.PI - 0.95, Math.PI + 0.95,
      );
      b.surf(SURF.webbing[0], SURF.webbing[1]);
      // Compression strap, wrapped round the bag rather than pasted on one face — built
      // from the bag's own rings, so it follows the taper and cannot float.
      b.loft([
        { ...packProud(5, 0.004), y: packY + 0.300 },
        { ...packProud(5, 0.004), y: packY + 0.288 },
      ], 20, K.packWebbing);
      // The pocket zip, and its pull.
      for (let k = 0; k < 6; k++) {
        loftPoint(packProud(6, 0.009), Math.PI - 0.9 + (1.8 * k) / 6, 0, _sa);
        loftPoint(packProud(6, 0.009), Math.PI - 0.9 + (1.8 * (k + 1)) / 6, 0, _sb);
        b.seg(_sa.x, _sa.y + 0.024, _sa.z, _sb.x, _sb.y + 0.024, _sb.z,
          0.005, K.packDark, 0.005, 5);
      }
      b.box(0.013, 0.022, 0.011, -0.158, packY + 0.264, 0.030, K.accent, 0.06);
      // Shoulder straps: over the deltoids and down the chest. These do more for the
      // "body in clothes" read than the pack itself does, because a strap crossing a
      // form is what tells the eye the form is there — and with the jersey now the light
      // field, a dark strap running across it is one of the two hard lines on the back.
      for (const sz of [1, -1]) {
        const strap: [number, number, number][] = [
          [-0.108, packY + 0.352, sz * 0.030],
          [-0.062, 0.436, sz * 0.082],
          [0.010, 0.454, sz * 0.098],
          [0.078, 0.368, sz * 0.098],
          [0.088, 0.252, sz * 0.080],
        ];
        for (let i = 0; i < strap.length - 1; i++) {
          b.rod(strap[i][0], strap[i][1], strap[i][2],
            strap[i + 1][0], strap[i + 1][1], strap[i + 1][2],
            0.017, K.packWebbing, 0.015, 6);
        }
        b.box(0.024, 0.040, 0.032, 0.086, 0.300, sz * 0.084, K.accent, 0.10);
      }
      // Hose off the left shoulder — small, but it is the one asymmetric thing on the
      // rider and asymmetry is most of what stops a model reading as a mannequin.
      b.seg(-0.114, packY + 0.352, -0.040, -0.056, 0.420, -0.092, 0.007, K.packDark, 0.007, 5);
      b.seg(-0.056, 0.420, -0.092, 0.044, 0.396, -0.102, 0.007, K.packDark, 0.007, 5);

      // --- the creases --------------------------------------------------------------
      // Every one of these is a place a critique named. They cost nothing at runtime.
      b.shade(0.010, 0.122, 0, 0.145, 0.46);            // under the jersey hem
      for (const sz of [1, -1]) {
        b.shade(0.000, -0.052, sz * 0.104, 0.140, 0.48); // hip/thigh crease
        b.shade(0.010, 0.420, sz * 0.134, 0.130, 0.42);  // armpit
        // The seam valley, both sides of the welt. This is the crease that makes the
        // two seat panels read as two panels when the rider's back is in shadow.
        b.shade(-0.084, 0.010, sz * 0.018, 0.064, 0.52);
        b.shade(-0.098, -0.040, sz * 0.020, 0.058, 0.46);
        // Under the shoulder straps, where they cross the jersey.
        b.shade(0.030, 0.430, sz * 0.100, 0.062, 0.40);
      }
      // The slot between the pack and the back — the hardest edge on the model, and the
      // only reason the pack reads at all on the shaded side of a rider.
      //
      // These probes sit ON the jersey surface, not inside the pack. The first version
      // put a 115 mm probe at the pack's own centre of mass and crushed the whole bag to
      // near-black, which is precisely the failure the pack was rebuilt to fix — a probe
      // is contact occlusion, and contact occlusion belongs in the gap.
      b.shade(-0.082, packY + 0.240, 0, 0.048, 0.55);   // under the pack
      for (const sz of [1, -1]) b.shade(-0.078, packY + 0.298, sz * 0.048, 0.040, 0.45);
      b.shade(-0.084, packY + 0.354, 0, 0.042, 0.36);   // behind the lid
      // Under the collar's roll, so the one lit horizontal plane at the neck has an edge
      // below it and does not simply fade into the jersey it is made of.
      b.shade(0.006, S.torso + 0.026, 0, 0.070, 0.34);
      b.shade(-0.072, -0.096, 0, 0.150, 0.42);          // seat, over the saddle
      // The crotch. With the bridging ellipsoid gone this is an open slot, and the two
      // surfaces bounding it — the underside of the seat and the inner face of each thigh
      // — are the ones the sun never reaches. A hard crease here is what makes the notch
      // between the legs read as depth rather than as a paint change, and it is the
      // torso's half of the pair; the thigh carries the matching one.
      for (const sz of [1, -1]) b.shade(-0.030, -0.084, sz * 0.052, 0.086, 0.50);
      b.shade(0.020, S.torso + 0.020, 0, 0.100, 0.38);  // under the jaw
      skin.part(b.build(), iTorso);
    }

    // --- helmet: A FULL FACE. +X is the look direction -------------------------------
    //
    // ### The gap this is answering, and why nothing smaller could
    //
    // Wave 4 round 1 scored the rider 6 and named one thing:
    //
    //   "the head is still a sphere rather than the decided full-face-with-goggles: from
    //    every shipped camera angle it reads as a dark egg with two ~200-luma specular
    //    pips side by side at eye height and orange lobes at the temples (i.e. a face),
    //    the chin bar is authored in the shell's own colour so it never separates in
    //    silhouette, and the loudest element on the head is the orange peak tip, which
    //    from three-quarter reads as a beak."
    //
    // ### How this was measured, including the instrument that had to be thrown away
    //
    // `captures/w4-head-view.mjs` isolates the head exactly — it renders the kit silhouette
    // twice, once with the head BONE collapsed to zero scale, and differences the two, so
    // "is this pixel head" is never a colour guess. Two things it reports are trustworthy
    // and one is not, and the difference matters:
    //
    // - **Rendered luma and hue inside that mask are only comparable WITHIN one frame.**
    //   Nine agents are editing the world under the spawn, so the sun angle on the parked
    //   bike is not the same twice; the probe forces the harness's own `parked` input so
    //   the pose repeats, and prints the whole kit's luma beside the head's so the head can
    //   be read against the rest of the rider rather than against another run.
    // - **The rendered front-outline scan does not discriminate and was discarded.** It was
    //   written to be the proof of the shape change, and run against the round-1 build it
    //   reports a 72 mm "eyeport recess" and a +37 mm "chin jut" — indistinguishable from
    //   what it reports here. It was measuring the old peak and the old goggle box, both of
    //   which stuck out of the egg, and it is confounded by perspective and head pitch. The
    //   wave brief's standing warning about measurement bugs producing wrong conclusions
    //   applies to instruments an author writes to prove their own change, most of all.
    // - **What replaced it has no camera and no light in it.** The probe walks the shipped
    //   skinned buffer, takes every vertex whose dominant bone is the head, and expresses
    //   it in the head bone's own frame. That is the helmet's own profile in millimetres.
    //
    // So the falsifiable claims below are about *geometry and albedo on the buffer*, which
    // cannot move with the weather, and the rendered numbers are quoted only as within-frame
    // ratios.
    //
    // ### What is built
    //
    // **The shell is ONE loft, crown to chin bar.** Fifteen cross-sections. `cx` is what
    // does the work: the front of each ring is `cx + rx` and the back is `cx - rx`, so the
    // two profiles are authored independently out of one closed section. The back runs
    // smoothly from the crown over the occiput and then CUTS AWAY for the neck
    // (-50 -> -140 -> -6 mm); the front does brow +118, eyeport +64, chin bar +138 — a
    // **54 mm eyeport recess and a 74 mm chin projection in the shell itself**, on the
    // surface, not painted.
    //
    // WAVE 5 rebuilt the half of this the chase camera can see, and ROUND 2 rebuilt it
    // again — to a SIZE that survives being drawn. The shell's lateral half-width, crown
    // to chin, in millimetres:
    //
    //   round 1:  38 68 90 99 103 107 108 | 109 106  94 104 113 104  78  50  26
    //   round 2:  38 68 90 96  97 100 107 | 109 104  86 100 122 116  98  82  60
    //                           ^notch      ^goggle   ^ear  ^pod         ^chin bar
    //   peak tip:  round 1 132      round 2 145
    //
    // Both have the same three reversals. The difference is their DEPTH, which is the only
    // property that survives quantisation to a 29-68 px head:
    //
    //   peak proud of the shell beneath it   23 mm -> 47 mm    (45 needed, measured)
    //   ear pocket to cheek pod              19 mm -> 36 mm    (34 needed, measured)
    //   chin bar half-width at the bottom    26 mm -> 60 mm
    //
    // "Needed" is not an opinion — see `PEAK_PROUD` at the top of this file for where the
    // two numbers come from. Round 1's entire delivered change was under the resolution
    // limit of the instrument that certified it, and the wave-4 build it replaced ran
    // 54 74 86 91 96 99 102 104 106 107 108 109 ... 39 25: one rise and one fall, which is
    // an ellipse, which is what "a featureless dark egg" was measuring.
    //
    // It also has to be ONE surface. The round-1 head was five ellipsoids and three boxes,
    // and an ellipsoid resting on an ellipsoid leaves an intersection curve — the same
    // defect 6d fixed at the shoulder. A helmet is one moulded shell and this is now one.
    //
    // **The goggle is one dark band, bracketed by two light rails.** The lens is 0x1e232b
    // and spans the full eyeport; the rails above and below it are 0x767d86. So the head
    // reads, top to bottom: dark shell / light rail / DARK lens / light rail / dark chin
    // bar with a light mouthpiece on it. Five horizontal bands, all continuous across the
    // face — and nothing paired. That is the fix for "two pips side by side": a pair of
    // small bright spots at eye height is a face however it is modelled, and one wide
    // dark band with a rim is goggles however it is lit.
    //
    // The old pips were not even specular. They were the ENDS OF THE LENS: a 0.170 m box in
    // `K.goggle` 0x8fa8b2 — albedo 163, the lightest thing on the head — inside a shell only
    // 0.220 m wide, so its two corners came out through the temples at eye height. Measured
    // on the buffer now, **0.00% of the head's 0.404 m² of surface carries an albedo over
    // 140**, and the brightest thing anywhere on it is the 124 of the goggle rims. There is
    // nothing left that can render as a bright pip except a specular highlight, and
    // `SURF.shell` went 0.34 -> 0.55 so that highlight is a broad sheen across the crown
    // rather than a round dot.
    //
    // **The orange is one 10 mm stripe round the back of the strap.** Before: two accent
    // ellipsoids 140 x 48 x 60 mm on the temples, a 100 x 52 x 212 mm accent band round the
    // back, and a 52 x 18 x 205 mm accent slab on the peak tip — about 0.11 m² of saturated
    // orange on a head. Now **0.95% of 0.404 m², i.e. 0.004 m²**, all of it one stripe on
    // the REAR arc, which is the arc the chase camera actually sees.
    //
    // **The peak is wide, short and dark.** 60 mm of projection past the brow, and — as of
    // wave 5 — 23 mm WIDER than the shell at its tips, which is the point of it: round 3's
    // peak was authored no wider than the shell and therefore contributed exactly zero
    // pixels to the outline from the only camera the game uses. Long-and-narrow-and-bright
    // is a beak; short-and-wide-and-dark is a visor. It is the shell's own colour: on this
    // head the ONE loud element is the goggle, and it is loud by being dark.
    //
    // Cost: the whole rebuild is +2000 triangles and **zero draw calls** — bike + rider is
    // still 3 beauty + 1 shadow, re-measured with `captures/w4-rider-view.mjs`.
    {
      const b = new PartBuilder();
      // Sky occlusion on downward faces — the visor underside, the eyeport's bottom lip
      // and the under-jaw, which are the three edges that say "full face".
      //
      // 0.14, down from 0.22 and originally 0.34. Every reduction has been forced by the
      // same measurement: the head renders far darker than the body it is attached to, so
      // every crease put on it lands on a surface that has no light left to lose. Round 2
      // delivered p50 12 against a torso at 53. A head is a convex object lit almost
      // entirely by ambient from the chase camera's side of it, and taking 22% of that
      // ambient off every downward face was buying an edge at the eyeport by making the
      // whole rear of the shell unreadable. What replaces it is on the rear and it is
      // additive: a scallop, a vent lip and an occipital band, all LIGHTER than the shell.
      b.underShade(0.14);
      b.surf(SURF.shell[0], SURF.shell[1]);

      // The shell. `S.helmet` is the head radius the proportions table carries; the ring
      // table is written against it so the helmet scales with the rider rather than
      // drifting off on its own.
      const R = S.helmet;
      const r = (v: number) => v * (R / 0.116);
      const shell: LoftRing[] = [
        { y: r(0.132), cx: r(-0.014), rx: r(0.036), rz: r(0.038) },
        { y: r(0.122), cx: r(-0.014), rx: r(0.066), rz: r(0.068) },
        { y: r(0.106), cx: r(-0.014), rx: r(0.088), rz: r(0.090) },
        // --- WAVE 5 ROUND 2: THE CROWN IS NARROWER THAN THE PEAK, AND THAT IS THE POINT.
        // Round 1 authored a 23 mm peak on a shell that was 103 mm wide underneath it, and
        // 23 mm is under the delivered frame's resolution (see PEAK_PROUD). There are two
        // ways to buy a step and only one of them is honest at these proportions: making
        // the visor 300 mm wide would have been a caricature, so the crown gives up 6 mm
        // instead. It should: a full face is widest at the ear and the cheek, not at the
        // temple, and every reference in `assets/reference/target/` shows that.
        { y: r(0.086), cx: r(-0.012), rx: r(0.102), rz: r(0.096) },
        { y: r(0.060), cx: r(-0.008), rx: r(0.115), rz: r(0.097) },
        // Brow — the furthest forward the shell itself ever gets, and the bottom of the
        // notch behind the peak's tips.
        { y: r(0.034), cx: r(-0.004), rx: r(0.122), rz: r(0.100) },
        { y: r(0.018), cx: r(-0.010), rx: r(0.118), rz: r(0.107) },
        // ...and the eyeport, 58 mm back from it. The section stays WIDE here (rz is at
        // its maximum) because a full-face wraps the temples at exactly the height it is
        // cut away at the front; shrinking the whole ring would have made a waist.
        //
        // This ring's `rz` is LOAD-BEARING FOR THREE OTHER PARTS and must not be moved
        // casually: the goggle lens, both goggle rails and the strap are all authored a
        // few millimetres outside it, and round 3 measured what happens when one of them
        // stands 6.5 mm proud at the temple — it puts a flange on the silhouette exactly
        // where a hat brim would be. Everything wave 5 does to the outline is BELOW here.
        { y: r(0.002), cx: r(-0.034), rx: r(0.098), rz: r(0.109) },
        // --- WAVE 5: THE EAR POCKET AND THE JAW, AND THEY ARE IN `rz` -------------------
        //
        // Everything from here down used to fall away monotonically — 0.108, 0.101, 0.093,
        // 0.082, 0.064, 0.044, 0.026. A half-width that rises once and falls once IS an
        // ellipse, and that is why three rounds of pushing the shell's VALUE could not fix
        // "a featureless dark egg": `cx` carries the whole eyeport recess and the whole chin
        // projection and `cx` points straight away from the only camera the game uses, so
        // none of it projects. `rz` is the only column on this table the chase camera sees.
        //
        // It now goes 0.109 -> 0.094 -> 0.113 -> 0.026: a WAIST at the ear and a FLARE at
        // the jaw, which is where a full face is actually widest (the ear cover and the
        // cheek pad, not the temple). Three direction changes, measured by
        // `rider.helmetOutlineReversals` below, against the one an ellipse has.
        //
        // The waist is 15 mm deep and it is what makes the jaw a *corner* rather than the
        // fat part of an oval. It is also why the ring immediately under the eyeport only
        // gives up 3 mm: the goggle strap runs at rz 0.110 through y -0.012, and round 3
        // measured what happens when a strap stands 6.5 mm proud at the temple — it puts a
        // hat brim on the silhouette. At 0.106 here the strap clears it by 2.8 mm.
        { y: r(-0.022), cx: r(-0.036), rx: r(0.096), rz: r(0.104) },
        { y: r(-0.038), cx: r(-0.028), rx: r(0.100), rz: r(0.086), drop: r(0.004) },
        { y: r(-0.054), cx: r(-0.006), rx: r(0.108), rz: r(0.100), drop: r(0.011) },
        { y: r(-0.068), cx: r(0.018), rx: r(0.113), rz: r(0.122), drop: r(0.018) },
        // --- AND THE CHIN BAR IS A BAR ------------------------------------------------
        // These four rings ran 0.104 / 0.078 / 0.050 / 0.026 and that is a cone: the head
        // came to a POINT at the chin, so from the chase camera the bottom of the helmet
        // tapered away into the collar and there was no chin bar in the outline at all —
        // which is one third of what the gap asked for and the third that no amount of
        // widening at the jaw could have supplied.
        //
        // A full face's chin bar is a blunt beam: it keeps most of the cheek's width all
        // the way down and ends in a flat edge. It is 120 mm across at the bottom now
        // against 52, and it is the widest thing on the head below the goggle after the
        // pod itself. Delivered, that is the difference between a head that ends in a
        // taper and one that ends in a squared-off jaw you can see at 29 px.
        { y: r(-0.082), cx: r(0.032), rx: r(0.106), rz: r(0.116), drop: r(0.019) },
        { y: r(-0.096), cx: r(0.036), rx: r(0.088), rz: r(0.098), drop: r(0.016) },
        { y: r(-0.109), cx: r(0.032), rx: r(0.062), rz: r(0.082), drop: r(0.010) },
        { y: r(-0.121), cx: r(0.024), rx: r(0.034), rz: r(0.060), drop: r(0.004) },
      ];
      b.loft(shell, 28, K.helmet, true);
      // Plug the opening the last ring leaves, looking straight up into the shell. It has
      // to match the ring above it: at the old 28 mm it would now be a bead hanging under
      // a 60 mm bar with a gap either side of it.
      b.blob(r(0.024), r(-0.126), 0, r(0.038), r(0.012), r(0.058), K.helmetLow, 10);

      // Occipital pad: the bulge at the back of a full face, a value step down INSIDE the
      // dark mass rather than a second colour. It sits high and tight — the first version
      // was 74 mm wide and hung to y = -0.062, which added exactly the rearward mass the
      // cutaway above exists to remove.
      b.blob(r(-0.080), r(-0.024), 0, r(0.046), r(0.042), r(0.072), K.helmetLow, 12, 0.14);

      // --- THE REAR OF THE SHELL --------------------------------------------------------
      //
      // This is the wave-4 round-3 gap, and it is the one part of the head the chase camera
      // ever looks at. Round 2 built a genuine full face — a 58 mm eyeport recess, a 70 mm
      // chin projection, a five-band goggle — and put every one of those features on the
      // FRONT, where the player never sees them. Measured on the delivered frames the head
      // came back p50 12 / p95 47 against a torso at 53: a flat dark dome with one glossy
      // sheen on the crown and a dead-straight orange strap line underlining it. A dome
      // with a straight line across its base is a brimmed hat, and that is what it read as.
      //
      // Everything below is on the rear arc, and every element is LIGHTER than the shell,
      // because a step down inside a mass rendering at 12 is not a step at all.
      //
      // The features are placed by evaluating the shell's own section at the height they
      // reach, not by writing a ring table next to it and hoping — the same rule the seat
      // yoke and the trouser welts follow, and it matters more here than anywhere else on
      // the model because the rear face of the shell moves 22 mm in x over the 60 mm the
      // spine runs through.
      //
      // The height it is indexed by is the ring's height AT THE REAR (`y + drop`), because
      // every feature below is specified by where it sits on the back of the head and the
      // lower rings are arched. The lerped drop rides along, so a point placed at theta PI
      // lands exactly at the height asked for and one placed off to the side follows the
      // same arch the shell does.
      const shellAt = (yRear: number): LoftRing => {
        const rearY = (k: number) => shell[k].y + (shell[k].drop ?? 0);
        let i = 0;
        while (i < shell.length - 2 && rearY(i + 1) > yRear) i++;
        const a = shell[i], c = shell[i + 1];
        const t = clamp01((rearY(i) - yRear) / Math.max(rearY(i) - rearY(i + 1), 1e-6));
        const d = lerp(a.drop ?? 0, c.drop ?? 0, t);
        return {
          y: yRear - d,
          cx: lerp(a.cx ?? 0, c.cx ?? 0, t),
          rx: lerp(a.rx, c.rx, t),
          rz: lerp(a.rz, c.rz, t),
          drop: d,
        };
      };
      const shellPt = (y: number, th: number, off: number, out: THREE.Vector3) =>
        loftPoint(shellAt(y), th, off, out);

      /**
       * The same lookup indexed by the ring's height AT THE FRONT (`y - drop`), for the
       * features that live on the chin bar and the brow.
       *
       * It exists because wave 5 moved the whole lower shell — the jaw flare is 31 mm wider
       * and the chin bar's front face 8 mm further forward — and the mouthpiece and the brow
       * vent were both hard-coded ring tables authored against the OLD profile. A grille
       * left on the old numbers hangs in front of the bar it is meant to be cut into, which
       * is the failure this file already recorded once when the shell moved in wave 4.
       */
      const shellFrontAt = (yFront: number): LoftRing => {
        const frontY = (k: number) => shell[k].y - (shell[k].drop ?? 0);
        let i = 0;
        while (i < shell.length - 2 && frontY(i + 1) > yFront) i++;
        const a = shell[i], c = shell[i + 1];
        const t = clamp01((frontY(i) - yFront) / Math.max(frontY(i) - frontY(i + 1), 1e-6));
        const d = lerp(a.drop ?? 0, c.drop ?? 0, t);
        return {
          y: yFront + d,
          cx: lerp(a.cx ?? 0, c.cx ?? 0, t),
          rx: lerp(a.rx, c.rx, t),
          rz: lerp(a.rz, c.rz, t),
          drop: d,
        };
      };
      /**
       * A ring lying `off` metres outside the shell's own section at that front height.
       *
       * `off` must never be zero or negative for anything that is meant to be SEEN. The
       * shell is 16 polygonal rings and a feature laid on it is 2-4; between two of its own
       * rings the shell's chord cuts inside the arc, so a band inset by 2 mm surfaces and
       * submerges alternately along its length and comes back as a row of torn shards. That
       * is what the round-1 capture of this wave showed round the mouthpiece, and it is the
       * same trap this file already recorded for the scallop trim.
       */
      const frontProud = (yFront: number, off: number): LoftRing => {
        const s = shellFrontAt(r(yFront));
        return { y: s.y, cx: s.cx, rx: s.rx + r(off), rz: s.rz + r(off), drop: s.drop };
      };
      /**
       * The shell's LATERAL half-width at a height — indexed by the ring's own `y`, because
       * `drop` contributes nothing at theta = +-PI/2.
       *
       * The goggle is authored front-first: `rx` is what seats it in the eyeport recess and
       * is deliberately tens of millimetres proud there. `rz` is a different question — it
       * decides where the loft's two open ENDS sit, out at the temple, and if they sit
       * outside the shell they are tabs. Wave 5 narrowed the shell by 15 mm at the ear to
       * put a waist in the outline, and the goggle's lower rail, authored against the old
       * profile, went from 8 mm proud to 16 mm and grew exactly those tabs.
       */
      const shellSideAt = (y: number): number => {
        let i = 0;
        while (i < shell.length - 2 && shell[i + 1].y > y) i++;
        const a = shell[i], c = shell[i + 1];
        const t = clamp01((a.y - y) / Math.max(a.y - c.y, 1e-6));
        return lerp(a.rz, c.rz, t);
      };
      const sideProud = (y: number, off: number) => shellSideAt(r(y)) + r(off);

      // --- a note on the scallop, which is NOT here ----------------------------------------
      // The occiput-to-neck scallop is in the shell rings above, as `drop`. Two attempts at
      // tracing it with a light trim rod were built and both were thrown away, and the
      // reason is worth keeping because it applies to every feature anyone lays on a curved
      // surface in this file:
      //
      //  - A tube whose axis follows a surface sits *inside* it between polyline points,
      //    because the chord cuts the corner off the arc. At 1 mm of clearance the run
      //    surfaced and submerged alternately and the helmet's lower edge came back as a
      //    ragged serrated line that reads as a modelling error.
      //  - Pushing it 8 mm proud fixed that and broke something worse. `shellAt` indexes on
      //    rear height, so at the ENDS of a wide rear arc it returns a chin-bar ring several
      //    centimetres narrower than the shell actually is out there, and both ends of the
      //    run came out as flat tabs floating off the temples in the profile and
      //    three-quarter captures.
      //
      // The scallop needs no trim: it is in the outline, and an outline needs no light. That
      // is also why it survives on the one part of the head the sun never reaches.

      // --- the rear exhaust, and it is VERTICAL ---------------------------------------------
      //
      // The first cut of this was the obvious one — a wide arched slot across the occiput
      // with a light lip under it, the brow vent mirrored to the back — and the capture
      // killed it immediately. At the ~45 px the head occupies from the chase camera the
      // arched dark slot broke into two dark marks side by side near the top of the dome and
      // read as a squinting pair of eyes on the back of the head: the exact round-1 failure
      // this piece has already been sent back for once, with the sign flipped. And any
      // horizontal band across a dome risks being read as a second brim, which is the thing
      // being fixed.
      //
      // A vent is therefore built the one way that can be neither: **one light centre spine,
      // running down the back of the shell**, with the two exhaust ports as narrow dark
      // slots hugging it. Vertical, so it cannot be a brim; singular, so it cannot be a
      // pair; and at chase distance the two dark slots merge into the shell they are cut in
      // and what survives is a single light stripe down the middle of the occiput — which is
      // the hard value step the gap asked for, and the only internal form a dome can have
      // that does not depend on where the sun happens to be.
      //
      // The spine is placed by walking the shell's own section down theta = PI, so it lies
      // on the surface across the 22 mm the rear face moves through over its length.
      const portRing = (y: number, off: number): LoftRing => {
        const s = shellAt(y);
        return { y, cx: s.cx, rx: s.rx + off, rz: s.rz + off };
      };
      b.surf(SURF.shell[0], SURF.shell[1]);
      for (const sz of [1, -1]) {
        b.loft([
          portRing(r(0.090), r(-0.002)), portRing(r(0.070), r(-0.002)),
          portRing(r(0.050), r(-0.002)), portRing(r(0.034), r(-0.002)),
        ], 4, K.trim, false,
        Math.PI + (sz > 0 ? 0.085 : -0.28), Math.PI + (sz > 0 ? 0.28 : -0.085));
      }
      b.surf(SURF.fabricTight[0], SURF.fabricTight[1]);
      const SP_N = 10;
      for (let i = 0; i < SP_N; i++) {
        const y0 = lerp(r(0.092), r(0.032), i / SP_N);
        const y1 = lerp(r(0.092), r(0.032), (i + 1) / SP_N);
        shellPt(y0, Math.PI, r(0.003), _sa);
        shellPt(y1, Math.PI, r(0.003), _sb);
        // Tapered, low, and it stops clear of both the crown and the strap. The first cut
        // ran parallel-sided from the crown to the strap 10 mm proud, and in the profile
        // capture it broke the shell's outline at the top and read as a fin bolted on
        // rather than as a ridge moulded in.
        b.seg(_sa.x, _sa.y, _sa.z, _sb.x, _sb.y, _sb.z,
          lerp(r(0.011), r(0.005), i / SP_N), K.helmetTrim,
          lerp(r(0.011), r(0.005), (i + 1) / SP_N), 8);
      }
      b.surf(SURF.shell[0], SURF.shell[1]);

      // --- the visor peak, AND ITS WINGS ------------------------------------------------
      //
      // Round 2 built a peak that projects 58 mm forward and is no wider than the shell.
      // Both halves of that were deliberate and the second half is the wave-5 gap: a peak
      // inside the shell's own width is, from behind, not there. Measured on the round-3
      // build the whole visor sat at |z| <= 0.106 against a shell at 0.109, so it
      // contributed exactly zero pixels to the outline from the one camera the game
      // actually uses. "Front-facing geometry the chase camera never sees" is precisely it.
      //
      // A real MTB full-face peak is WIDER than the shell and its outer tips sweep back
      // toward the temples. That is the whole fix: the blade's tips stand 23 mm outside the
      // widest section of the head, at brow height, with the dome rising between them — so
      // the outline goes out, in, out. Measured on the shipped buffer the half-width runs
      // 101 mm at the crown shoulder, 131 at the peak, 107 in the notch behind it: the
      // corner and the notch behind it are both in the silhouette, and both survive being
      // quantised to the ~50 px the head actually occupies in the delivered frame.
      //
      // It cannot be read as a brim, which is what round 3 was sent back for: a brim is one
      // dead-level line across the widest part of a dome, and this is a raked blade above
      // the widest part with a notch behind each tip.
      //
      // It is NOT two spurs plus a centre tongue. That was the first cut of this wave, as
      // three overlapping blobs, and it was abandoned before it was captured for a reason
      // worth keeping: an ellipsoid's extent under a yaw is a trigonometric function of
      // three of its axes, so placing a tip AT a target width means solving for the centre,
      // and every adjustment to the sweep moves the tip. It was also three intersecting
      // surfaces where a peak is one moulded part — the same defect the shoulder and the
      // helmet shell were both rebuilt to remove.
      //
      // It is ONE loft and it is four rings, which is the only way this shape can be both
      // a real blade and a single surface:
      //
      //   root-top -> outer-top -> outer-bottom -> root-bottom
      //
      // Ring 0 and ring 3 lie INSIDE the shell (rz 0.095 against a shell at 0.103) and
      // rings 1 and 2 are the peak's own outer boundary, so the loft sweeps a top face, a
      // 12 mm outer edge and an underside, and its two open ends are buried in the shell.
      // The docstring on `loft` says a profile may turn back on itself; this is that, and
      // it is the same construction the goggle rails use.
      //
      // **The outer ellipse is offset FORWARD (cx 0.075), and that is the whole trick.**
      // Two concentric ellipses never meet, so a peak authored on the shell's own centre
      // can never come back into the shell and has to be capped with a tab — which is what
      // put "two small horns bolted to the temples" in the round-3 capture. Offset forward,
      // the outer curve leaves the shell at the brow, stands 29 mm proud of it at the
      // temple, and re-enters it at about 130 degrees on its own. Nothing to cap.
      //
      // **It is RAKED, not level.** `drop` is negative here, so the blade rises 18 mm at
      // the nose and falls 13 mm at the swept-back ends. A peak drawn at a constant height
      // is one dead-level line across the widest part of a dome, i.e. a hat brim, and that
      // is exactly what round 3 was sent back for. This one is a plane tilted up and
      // forward — from a camera 4-24 degrees ABOVE the head, which is where the chase
      // camera lives, its top face catches light the dome behind it does not.
      // --- WAVE 5 ROUND 2: 47 mm PROUD, AND THE END ANGLE IS SOLVED, NOT CHOSEN --------
      //
      // Widening the blade is not free, and the constraint is the one this section already
      // records: the outer curve has to come back INSIDE the shell on its own or its two
      // ends need capping, and a capped end is the "two horns bolted to the temples" the
      // round-3 capture showed. Re-entry needs both
      //
      //     |rz_peak * sin(theta)| <= rz_shell        and       cx + rx*cos(theta) ~ cx_shell
      //
      // and the first of those is what a wider blade breaks: at 145 mm against a 98 mm
      // shell the lateral term alone does not come back inside until theta >= 132 deg. So
      // the forward offset is solved for that angle instead of picked — cx 0.070 with
      // rx 0.103 puts the fore-aft term at zero at 140 deg, where the lateral term is
      // 0.95, and the loft is carried on to 155 deg so the last 15 deg of it is buried
      // rather than flush. Nothing to cap, and the tip sweeps BACK toward the ear, which
      // is what a real visor's tip does.
      //
      // It cannot be read as a brim: a brim is one level line across the widest part of a
      // dome, and this is 47 mm proud of a shell that is itself narrower here than it is
      // at the ear, raked 24 mm nose-up, with the crown rising between the tips and a
      // notch cut behind each one.
      const pkDrop = r(-0.024);
      b.loft([
        { y: r(0.058), cx: r(-0.006), rx: r(0.106), rz: r(0.093), drop: pkDrop },
        { y: r(0.058), cx: r(0.070), rx: r(0.103), rz: r(0.145), drop: pkDrop },
        { y: r(0.040), cx: r(0.070), rx: r(0.103), rz: r(0.145), drop: pkDrop },
        { y: r(0.040), cx: r(-0.006), rx: r(0.106), rz: r(0.093), drop: pkDrop },
      ], 30, K.helmet, false, -2.70, 2.70);
      {
        // --- the build invariants, measured off what was just authored -----------------
        //
        // All three are `phase: 'build'`, so they are taken once as the kit is constructed
        // and never reset. They are deliberately measured on the RING TABLE, which is the
        // authored surface — and that is also their known limit: a ring table cannot know
        // what the collar occludes or what survives to 29 px. That half is
        // `captures/w5-rider-tools/headpx.mjs`, and the budgets here are its output.
        let reversals = 0;
        for (let k = 1; k < shell.length - 1; k++) {
          const a = shell[k].rz - shell[k - 1].rz;
          const c = shell[k + 1].rz - shell[k].rz;
          if (a * c < 0) reversals++;
        }
        HELMET_REVERSALS.observeWhere(reversals, 'helmet shell rz profile');
        // The shell's own half-width at the peak's height — the thing the blade has to
        // stand out of. Not the widest ring on the head: the cheek pod is wider than the
        // shell is up here, and measuring against it would let a peak that is invisible
        // from behind pass because the jaw happens to be narrow.
        const peakY = r(0.049);
        let sAtPeak = shell[0].rz;
        for (let k = 0; k < shell.length - 1; k++) {
          if (shell[k].y >= peakY && shell[k + 1].y <= peakY) {
            const t = (shell[k].y - peakY) / Math.max(shell[k].y - shell[k + 1].y, 1e-6);
            sAtPeak = lerp(shell[k].rz, shell[k + 1].rz, t);
          }
        }
        // The blade's widest point is its outer ring at theta = PI/2, inside the lofted
        // arc, so this is a point that really is on the shipped surface.
        PEAK_PROUD.observeWhere((r(0.145) - sAtPeak) * 1000, 'visor peak outer ring at theta PI/2');
        // The ear pocket is the minimum of the shell below the goggle; the cheek pod is
        // the maximum below that. Both are found rather than hard-coded so this keeps
        // measuring the right thing when the table moves again.
        let earMin = Infinity, earAt = -1;
        for (let k = 0; k < shell.length; k++) {
          if (shell[k].y < r(-0.010) && shell[k].y > r(-0.060) && shell[k].rz < earMin) {
            earMin = shell[k].rz; earAt = k;
          }
        }
        let podMax = 0;
        for (let k = earAt + 1; k < shell.length; k++) if (shell[k].rz > podMax) podMax = shell[k].rz;
        JAW_FLARE.observeWhere((podMax - earMin) * 1000, 'shell ear pocket to cheek pod');
      }

      // --- the goggle ------------------------------------------------------------------
      // theta = 0 is straight ahead; this is the front arc, wrapping just past the temples
      // where the strap picks it up.
      //
      // Two proportions in here were got wrong once each and both were caught in a capture:
      //
      //  - **The lens is the tall band and the rails are thin.** With the lens and the two
      //    rails at similar heights the eyeport came back reading as three stacked stripes
      //    rather than as a goggle with a rim round it. The lens now shows about twice the
      //    height of either rail.
      //  - **The rails are proud in X and nearly flush in Z.** They started 11 mm over the
      //    shell laterally, which put three parallel ridges round the temple where there
      //    should be one goggle disappearing under one strap — from dead side-on it read as
      //    a ribbed tube. `rx` is what seats the goggle in the eyeport; `rz` only decides
      //    how loud it is at the side, and 3 mm is enough to catch an edge.
      const gF = -1.34, gB = 1.34;
      b.surf(SURF.lens[0], SURF.lens[1]);
      b.loft([
        { y: r(0.014), cx: r(-0.032), rx: r(0.108), rz: r(0.104) },
        { y: r(0.002), cx: r(-0.026), rx: r(0.124), rz: r(0.112) },
        { y: r(-0.018), cx: r(-0.026), rx: r(0.124), rz: sideProud(-0.018, 0.004) },
        { y: r(-0.032), cx: r(-0.032), rx: r(0.106), rz: sideProud(-0.032, 0.004) },
      ], 22, K.goggle, false, gF, gB);
      // The two frame rails. Each one flares OUT over the lens and steps back in behind
      // it, so it is a real overhang with an occluded edge under it rather than a stripe.
      //
      // They are FABRIC, not shell. A goggle frame is moulded plastic with a foam face —
      // at the shell's 0.55 roughness and 124 albedo the two rails were the glossiest and
      // the lightest thing on the head and came back reading as a pair of chrome tubes.
      b.surf(SURF.fabricTight[0], SURF.fabricTight[1]);
      b.loft([
        { y: r(0.020), cx: r(-0.030), rx: r(0.100), rz: r(0.100) },
        { y: r(0.013), cx: r(-0.028), rx: r(0.120), rz: r(0.110) },
        { y: r(0.008), cx: r(-0.026), rx: r(0.128), rz: r(0.113) },
        { y: r(0.004), cx: r(-0.026), rx: r(0.123), rz: r(0.111) },
      ], 22, K.goggleRim, false, gF, gB);
      b.loft([
        { y: r(-0.026), cx: r(-0.026), rx: r(0.123), rz: sideProud(-0.026, 0.003) },
        { y: r(-0.033), cx: r(-0.028), rx: r(0.128), rz: sideProud(-0.033, 0.003) },
        { y: r(-0.041), cx: r(-0.028), rx: r(0.112), rz: sideProud(-0.041, 0.003) },
        { y: r(-0.048), cx: r(-0.022), rx: r(0.092), rz: sideProud(-0.048, 0.002) },
        // Narrower than the top rail: a goggle's lower frame stops at the cheekbone while
        // the upper one carries on to the strap anchor.
      ], 22, K.goggleRim, false, gF + 0.18, gB - 0.18);

      // --- the strap --------------------------------------------------------------------
      // The REAR arc, which is the one the chase camera sees. Near-black webbing standing
      // 7 mm off the shell, with the single saturated note on the whole head — one 10 mm
      // stripe — laid inside it.
      b.surf(SURF.webbing[0], SURF.webbing[1]);
      // It starts INSIDE where the goggle rails end (1.34), so the two lofts overlap by
      // about 7 mm at each temple. Butted exactly, the strap's open end left a small
      // square tab standing off the side of the shell at eye height in the dead-astern
      // frame — a fleck, but a fleck at eye height on a head is the one place this piece
      // cannot afford one.
      //
      // **Wave 4 round 3: it ARCHES, and it is no longer the widest thing on the head.**
      // Two properties of the round-2 strap were, between them, the whole "brimmed hat"
      // read. It ran dead level round the shell — one straight horizontal line across the
      // widest part of a dome, with the single orange stripe on the model laid inside it,
      // so the eye got a hat brim underlined in orange. And at `rz` 0.115 against a shell
      // whose widest section is 0.109 it stood 6.5 mm proud at both temples, which put a
      // flange on the silhouette exactly where a brim would be.
      //
      // A real goggle strap climbs from the buckle at the temple to sit high on the back of
      // the shell. `drop` with `dropAt` 0 is that climb: 26 mm from the ear to dead rear,
      // so the line now curves with the occiput instead of cutting across it, and the
      // orange curves with it. 1.5 mm proud at the temple is enough to catch an edge.
      const sF = 1.28, sB = Math.PI * 2 - 1.28;
      /** Rise from the buckle to dead rear, m, and the y offset that keeps the ends put. */
      const sArch = r(0.026) / (1 + Math.cos(1.28));
      const sY = (y: number) => r(y) + sArch * Math.cos(1.28);
      // **WAVE 5 ROUND 2: its `rz` FOLLOWS THE SHELL, it is not a constant.**
      //
      // This is the single most expensive thing found this round, and it was found only by
      // measuring the delivered outline rather than the ring table. Round 2 cut a 36 mm ear
      // pocket into the shell — and the delivered silhouette did not move there at all,
      // because the strap ran at a flat `rz` 0.110 through exactly those heights and stood
      // 24 mm PROUD of the new pocket. A 12 mm band of near-black webbing was the outline
      // for the whole of the shell's most expensive feature, and the ring table could not
      // have said so: on the table the pocket was 36 mm deep and correct.
      //
      // This file already carried the warning ("round 3 measured what happens when a strap
      // stands 6.5 mm proud at the temple — it puts a flange on the silhouette") and the
      // tool for it (`sideProud`, written for the goggle rails for the same reason), and
      // the strap was still authored against a profile that had moved. Anything laid on
      // this shell laterally must be indexed off it.
      b.loft([
        { y: sY(0.016), cx: r(-0.034), rx: r(0.098), rz: sideProud(0.016, 0.000), drop: sArch },
        { y: sY(0.004), cx: r(-0.034), rx: r(0.102), rz: sideProud(0.004, 0.0015), drop: sArch },
        { y: sY(-0.018), cx: r(-0.034), rx: r(0.102), rz: sideProud(-0.018, 0.0015), drop: sArch },
        { y: sY(-0.030), cx: r(-0.034), rx: r(0.098), rz: sideProud(-0.030, 0.000), drop: sArch },
      ], 22, K.strap, false, sF, sB);
      // The orange, and WAVE 5 ROUND 2 cuts it again — to 0.18 rad, and taller than it is
      // long. Wave 4 shortened this from temple-to-temple to a 0.86-rad arc and called it a
      // badge; the delivered 8x crop of the round-1 build says otherwise. At 0.86 rad the
      // chord is 160 mm on a 240 mm head and it renders as **a saturated horizontal bar
      // across the widest part of the shell** — which is the wave-4 gap ("a dead-straight
      // orange strap line underlining it") surviving at two thirds of its old length,
      // because what makes a brim is the line's aspect ratio and its height on the dome,
      // not its absolute length.
      //
      // 0.18 rad is a 38 x 16 mm patch: 2.4:1 rather than 10:1, so the eye reads a buckle
      // cover on the strap. It is also the only saturated note on the head and it stays,
      // because a head with no accent at all reads as a prop rather than as kit.
      b.loft([
        { y: sY(0.000), cx: r(-0.034), rx: r(0.105), rz: sideProud(0.000, 0.004), drop: sArch },
        { y: sY(-0.016), cx: r(-0.034), rx: r(0.105), rz: sideProud(-0.016, 0.004), drop: sArch },
      ], 8, K.accent, false, Math.PI - 0.18, Math.PI + 0.18);
      // The buckle, where the strap leaves the shell on the left. One asymmetric object,
      // for the same reason the hydration hose is on the pack.
      b.box(r(0.016), r(0.024), r(0.012), r(-0.050), r(-0.007), r(-0.114), K.strap, 0.2);

      // --- the mouthpiece ---------------------------------------------------------------
      // The chin bar's own feature, and the second thing on the head that is not the shell
      // value. A dark recessed grille with two mid-grey bars across it: on a dark shell a
      // dark slot is nothing (round 1 learned that on the crown vents), so what reads has
      // to be the bar, not the gap.
      //
      // RE-SEATED IN WAVE 5, and it had to be: the chin bar's front face moved forward
      // and down when the shell's lower rings were rebuilt, and a grille left on the old
      // profile would have hung 20 mm out in front of the bar it is supposed to be cut
      // into. Every ring below is the shell's own front profile (`cx + rx` at theta 0)
      // minus 2 mm, and the two bars are the same profile plus 4 mm.
      b.surf(SURF.shell[0], SURF.shell[1]);
      const mF = -0.78, mB = 0.78;
      // Every ring is the shell's own section at that FRONT height, so when the chin bar
      // moves again the grille moves with it. The bezel stands 2 mm PROUD and the two bars
      // 7 mm — see `frontProud`: the first cut of this inset the bezel by 2 mm on the theory
      // that a vent is a recess, and it came back as a row of torn shards because a 4-ring
      // band cannot stay inside a 16-ring shell along a chord.
      b.loft([
        frontProud(-0.058, 0.002), frontProud(-0.074, 0.002),
        frontProud(-0.094, 0.002), frontProud(-0.110, 0.002),
      ], 16, K.trim, false, mF, mB);
      b.surf(SURF.fabricTight[0], SURF.fabricTight[1]);
      for (const yv of [-0.072, -0.096]) {
        b.loft([
          frontProud(yv + 0.005, 0.007), frontProud(yv - 0.004, 0.007),
        ], 16, K.goggleRim, false, mF, mB);
      }
      b.surf(SURF.shell[0], SURF.shell[1]);

      // --- brow vent ---------------------------------------------------------------------
      // One wide intake across the brow, with a mid-grey lower lip so it reads as a slot
      // in a dark shell rather than as more dark shell. Placed off the shell's own front
      // profile for the same reason the grille is, and dropped 8 mm from where it was so it
      // clears the peak's underside — the blade above it is raked now and its trailing edge
      // sits where the old vent's upper lip was.
      b.loft([
        frontProud(0.040, 0.002), frontProud(0.030, 0.004),
      ], 16, K.trim, false, -0.62, 0.62);
      b.loft([
        frontProud(0.028, 0.006), frontProud(0.022, 0.005),
      ], 16, K.goggleRim, false, -0.62, 0.62);

      // --- the creases -------------------------------------------------------------------
      // The eyeport is a recess, and a recess that is not occluded is a paint job. These
      // four are what make the brow read as an overhang from the side and the goggle read
      // as sitting inside the shell rather than on it.
      b.shade(r(0.104), r(0.042), 0, r(0.070), 0.38);           // under the visor
      b.shade(r(0.062), r(0.016), 0, r(0.052), 0.30);           // eyeport, under the brow
      for (const sz of [1, -1]) {
        b.shade(r(0.014), r(0.004), sz * r(0.100), r(0.042), 0.26);  // eyeport, temples
        b.shade(r(-0.050), r(0.024), sz * r(0.104), r(0.034), 0.14); // over the strap
        // WAVE 5. Two new creases, and both are under a new OUTLINE feature rather than
        // being a value move of their own: the eye finds an edge fastest where a form
        // turns away from the light AND the silhouette changes direction at the same
        // place. Kept weak (0.30 / 0.24) for the reason the three rear creases are weak —
        // the shell has very little light to lose.
        // Both moved out with the outline they sit under — a crease left on the old
        // profile lands in the middle of a face instead of on the turn, and this file has
        // already lost a round to exactly that when the mouthpiece stayed put.
        b.shade(r(0.030), r(0.030), sz * r(0.126), r(0.050), 0.32);   // under the peak's wing
        b.shade(r(-0.018), r(-0.038), sz * r(0.082), r(0.040), 0.26); // in the ear pocket
        b.shade(r(0.020), r(-0.070), sz * r(0.118), r(0.044), 0.22);  // over the cheek pod
      }
      b.shade(r(0.072), r(-0.050), 0, r(0.052), 0.28);          // eyeport bottom lip
      b.shade(r(0.058), r(-0.126), 0, r(0.072), 0.36);          // under the chin bar
      // The three rear creases are all deliberately WEAK. Everything they sit on is already
      // the darkest thing in the frame; their whole job is to give the scallop and the vent
      // lip an edge to be lit against, not to take more light off a shell that has none.
      b.shade(r(-0.118), r(0.062), 0, r(0.034), 0.22);          // in the exhaust ports
      b.shade(r(-0.030), r(-0.100), 0, r(0.070), 0.20);         // the neck cutaway
      skin.part(b.build(), iHead);
    }

    // --- limbs ---------------------------------------------------------------------
    for (let i = 0; i < 2; i++) {
      // Which way the *inside* of this limb faces in its own bone space. Bones are
      // aimed with +X forward, so local +Z is world +Z; index 0 is the left limb and
      // sits at -Z, so its inner face is +Z.
      const inZ = i === 0 ? 1 : -1;

      // --- shoulder and upper arm ----------------------------------------------------
      // THIS is the deltoid fix. The cap used to be a jersey ball on the torso with a
      // second jersey ball on the arm sitting inside it: two spheres of the same colour
      // pivoting through each other, which is exactly the "mannequin built from separate
      // balls" the critique measured. There is now ONE shoulder mass, authored on the
      // arm and weighted across the ramp below, so the top of it belongs to the torso,
      // the bottom belongs to the arm, and the surface between them bends instead of
      // sliding. It costs nothing: the weights ride in an attribute.
      const shoulderRamp: SkinRamp = {
        bone: iTorso,
        ox: 0, oy: 0, oz: 0,
        dx: 0, dy: -1, dz: 0,
        d0: -0.055, d1: 0.075,
      };
      const ua = new PartBuilder();
      ua.underShade(0.26);
      ua.surf(SURF.fabric[0], SURF.fabric[1]);
      // ROUND 3: the cap is a LOFT, and it starts INSIDE the torso.
      //
      // The ramp above has weighted this mass across two bones since round 1, and from
      // dead astern it still read as "two teal balls flanking a teal torso" — because
      // blending the *skinning* does nothing about the surface. An ellipsoid resting on
      // another ellipsoid leaves an intersection curve, and an intersection curve at the
      // shoulder is precisely the crease the eye calls "assembled balls" however the
      // vertices are weighted.
      //
      // A loft has no intersection to leave. This one runs from a small ring buried near
      // the base of the neck, out and down over the trapezius, through the deltoid's own
      // cap and on into the sleeve as ONE surface, with the section drifting outboard as
      // it goes (`cz`). Its top two rings sit past the ramp's far end, so they are pure
      // torso and behave as part of the body; the cap is 27% arm, the mid-deltoid 72%,
      // and the sleeve is all arm. Nothing crosses anything.
      ua.loft([
        { y: 0.074, rx: 0.038, rz: 0.042, cz: inZ * 0.056 },
        { y: 0.046, rx: 0.059, rz: 0.061, cz: inZ * 0.032 },
        { y: 0.010, rx: 0.072, rz: 0.072, cz: inZ * 0.008 },
        { y: -0.028, rx: 0.073, rz: 0.071, cz: -inZ * 0.004 },
        { y: -0.070, rx: 0.068, rz: 0.066, fold: 0.008, foldPhase: inZ * 0.5 },
        { y: -0.104, rx: 0.062, rz: 0.060, fold: 0.012, foldPhase: inZ * 0.5 },
        { y: -0.126, rx: 0.057, rz: 0.056 },
      ], 20, K.jersey, true);
      // Raglan seam: a welt where the sleeve is stitched to the body, which is the one
      // line that tells the eye a sleeve is a separate piece of cloth.
      ua.surf(SURF.fabricTight[0], SURF.fabricTight[1]);
      ua.blob(0.002, -0.052, 0, 0.072, 0.011, 0.069, K.jerseyDark, 12);
      // --- SLEEVE HEM, AND THE LAYER UNDER IT ----------------------------------------
      // The wave-3 gap: "a forearm is a single lit cone... the only large unclothed
      // masses on the model are also the only parts with no garment logic on them at
      // all." The cause was that BAR's decided archetype — jersey over a long-sleeve
      // base layer — was never built, so there was nothing for the arm to be dressed in.
      //
      // The sleeve now ENDS, at 42% of the upper arm, with a flared hem that stands 4 mm
      // proud of the sleeve and a hard occlusion crease under it. That flare matters: an
      // outer layer has to be visibly *outside* the layer it covers or the two read as
      // one tube with a stripe on it, which is the same failure as the painted seat panel
      // one wave ago.
      ua.surf(SURF.fabricTight[0], SURF.fabricTight[1]);
      ua.blob(0, -0.124, 0, 0.063, 0.017, 0.063, K.jerseyDark, 12);
      // The base layer, running out from under the hem and on down past the elbow. A
      // loft rather than a cone: a sleeve on an arm is closest at the wrist end of each
      // segment and creases where the muscle is, and those folds are the only thing that
      // makes a 45 mm cylinder catch light in more than one band.
      ua.surf(SURF.fabricTight[0], SURF.fabricTight[1]);
      ua.loft([
        { y: -0.098, rx: 0.055, rz: 0.055 },
        { y: -0.146, rx: 0.051, rz: 0.051, fold: 0.014, foldPhase: inZ * 0.6 },
        { y: -0.206, rx: 0.047, rz: 0.047, fold: 0.020, foldPhase: inZ * 0.6 },
        { y: -0.252, rx: 0.045, rz: 0.045, fold: 0.012, foldPhase: inZ * 0.6 },
        { y: -0.278, rx: 0.044, rz: 0.044 },
      ], 18, K.base);
      ua.shade(0, -0.060, 0, 0.062, 0.44);   // under the raglan seam
      ua.shade(0, -0.140, 0, 0.062, 0.50);   // under the sleeve hem — the layer edge
      skin.part(ua.build(), iArm[i], shoulderRamp);

      // --- forearm: base layer, and an elbow that is one form ------------------------
      // The forearm carries the pad now, and it carries it across the joint: the ramp
      // below hands the top 88 mm of this part to the upper arm, so the sleeve bends
      // through the elbow instead of two tubes sliding past each other. That is the same
      // move as the deltoid, one joint down, and it is what deletes the "pale bead at the
      // elbow" from the stack.
      const elbowRamp: SkinRamp = {
        bone: iArm[i],
        ox: 0, oy: 0, oz: 0,
        dx: 0, dy: -1, dz: 0,
        d0: -0.055, d1: 0.055,
      };
      const fa = new PartBuilder();
      fa.underShade(0.28);
      fa.surf(SURF.fabricTight[0], SURF.fabricTight[1]);
      // Tucked INSIDE the upper arm's sleeve at the top (0.043 against 0.044), so the
      // open end is never the thing you see at the elbow — the pad is.
      fa.loft([
        { y: 0.050, rx: 0.043, rz: 0.043 },
        { y: -0.014, rx: 0.048, rz: 0.048 },
        { y: -0.074, rx: 0.047, rz: 0.046, fold: 0.014, foldPhase: -inZ * 0.5 },
        { y: -0.136, rx: 0.043, rz: 0.042, fold: 0.018, foldPhase: -inZ * 0.5 },
        { y: -0.192, rx: 0.038, rz: 0.037, fold: 0.014, foldPhase: -inZ * 0.5 },
        { y: -0.232, rx: 0.035, rz: 0.034 },
      ], 18, K.base);
      // Wrist rib, where the base layer runs into the glove.
      fa.blob(0, -0.238, 0, 0.038, 0.016, 0.037, K.baseHi, 10);
      // Elbow cup, over the base layer and spanning the joint.
      fa.surf(SURF.armour[0], SURF.armour[1]);
      fa.blob(0.006, -0.022, 0, 0.052, 0.072, 0.050, K.pad);
      fa.blob(0.014, -0.030, 0, 0.036, 0.044, 0.034, K.padDark, 8);
      // Glove at the wrist — this is the part that has to land on the grip.
      fa.surf(SURF.fabricTight[0], SURF.fabricTight[1]);
      fa.blob(0, -S.foreArm + 0.026, 0, 0.049, 0.052, 0.041, K.glove);
      fa.box(0.032, 0.072, 0.062, 0.006, -S.foreArm + 0.052, 0, K.glove);
      // Tight, and only just under the cup's lower lip. The first version of this probe
      // was 70 mm at -0.086 and reached most of the sleeve: 44% off two thirds of the
      // forearm is not a crease, it is a coat of paint, and it is half of why the first
      // build of the base layer rendered black.
      fa.shade(0, -0.100, 0, 0.042, 0.42);   // under the elbow cup
      fa.shade(0, -0.246, 0, 0.048, 0.36);   // into the glove
      skin.part(fa.build(), iFore[i], elbowRamp);

      // --- hip and thigh -------------------------------------------------------------
      // THE WAVE-3 GAP, and it was named precisely: "each thigh is a straight-walled
      // cylinder the same diameter at hip and cuff, ending flat at mid-thigh with no
      // drape, no flare and no continuity into the hip, so the legs read as a stack of
      // disconnected primitives — dark seat ellipsoid, grey pipe, bare stick, pale bead
      // at the knee, stick, shoe."
      //
      // Every item on that list is a *silhouette* failure, so every answer here is
      // geometry rather than tone:
      //
      //   - the sleeve is a **loft**, not a cylinder: 0.086 m of radius where it leaves
      //     the hip, drawn in to 0.075 m where the cloth lies against the leg at
      //     mid-thigh, then flared to 0.093 m at the hem — against a leg that is 0.060 m
      //     there. A hem wider than the leg inside it is the single cue that says
      //     "cloth", and it was the one thing missing;
      //   - the hem **hangs**: it is 19 mm lower at the outer front than at the inner
      //     back, and it is a rolled edge — a lit crest, a shaded under-lip and an inner
      //     wall that runs back to the leg — instead of a flat ring;
      //   - it **drops over the knee pad**. It ends 0.10 m from the knee instead of at
      //     55% of the thigh, so the pad emerges from under the cloth and the bare stick
      //     between the two is gone. That deletes two items from the stack at once;
      //   - it is **continuous into the hip**. The top ring is small and tucked inside
      //     the hip mass, and the skin ramp below is long enough that everything down to
      //     the mid-thigh moves with the hips;
      //   - and it is **panelled**: a side seam and an inseam run its whole length, the
      //     seat panel carries down onto the back of the leg under a yoke, and there is a
      //     pocket on the outer thigh. Garments read as garments because they are made of
      //     pieces, and every one of those pieces here has a crest the sun can find.
      const hipRamp: SkinRamp = {
        bone: iTorso,
        ox: 0, oy: 0, oz: 0,
        dx: 0, dy: -1, dz: 0,
        d0: -0.052, d1: 0.104,
      };
      const th = new PartBuilder();
      th.underShade(0.30);
      th.surf(SURF.fabric[0], SURF.fabric[1]);

      /** Fold phase, mirrored per leg so the two legs are not the same object twice. */
      const fp = inZ * 0.7;
      // BAR: "Long trousers, not baggy shorts, with knee pads underneath." Round 2 built
      // the opposite of that first clause, and the cost was not stylistic: a hem at 73%
      // of the thigh is what CREATED the bare shin, because everything below it then had
      // to be either skin or armour. The leg is one garment from the waistband to the
      // ankle now — this part carries it to just above the knee and the shin part carries
      // it on down, with the two blended across the joint so it bends rather than
      // telescopes. Nothing about the upper leg's shaping changed; the rings below the
      // old hem are new.
      //
      // **WAVE 4 — the carried gap, and it is this part's to close.** "No inseam value
      // break and no background gap between the legs, so below the jersey the rider reads
      // as a sack rather than two legs at every camera angle."
      //
      // Both halves of that are answered here and neither is paint:
      //
      //   - **The section is narrowed laterally and drifted outboard.** It was very nearly
      //     circular — rz 0.086 against rx 0.081 — with the two bones only 0.180 m apart,
      //     so each leg's inner face landed at 0.004 m from the centreline and the two
      //     legs INTERSECTED. There was no gap to see because there was no gap. rz is now
      //     0.064–0.070 with the section drifting 10–14 mm outboard as it descends, and
      //     the pose splays the bones to 0.196 m, which opens roughly **80 mm of clear air
      //     between the two inner faces from the crotch to the knee**. rx is untouched, so
      //     the leg loses nothing in profile: it is an oval that is deeper than it is wide,
      //     which is also what a thigh is.
      //   - **The inner face is its own panel, at its own value.** The loft is built as two
      //     arcs sharing their edges: a gusset over the inner face in `trouserIn`, 21 luma
      //     under the field, and the field itself over the front, outside and back. A welt
      //     runs down each of the two joins. So the value break is a panel boundary on a
      //     real seam on a real surface — and it falls exactly on the two faces that look
      //     at each other across the new gap, which is what turns that gap from a slot into
      //     a shadow the eye can find at chase distance.
      const legRings: LoftRing[] = [
        // Buried in the hip mass. Small and drawn inboard so no rim can show.
        { y: 0.090, rx: 0.046, rz: 0.044, cz: inZ * 0.026 },
        { y: 0.034, rx: 0.070, rz: 0.064, cz: inZ * 0.006 },
        { y: -0.026, rx: 0.081, rz: 0.070, cx: 0.004, cz: -inZ * 0.008,
          fold: 0.010, foldPhase: fp },
        { y: -0.104, rx: 0.079, rz: 0.068, cx: 0.008, cz: -inZ * 0.012,
          fold: 0.018, foldPhase: fp },
        // The waist of the leg: where the cloth is closest to the thigh.
        { y: -0.180, rx: 0.075, rz: 0.065, cx: 0.011, cz: -inZ * 0.014,
          fold: 0.024, foldPhase: fp },
        // ...and then it fills again over the quad and drains toward the knee. A trouser
        // leg that only ever narrows reads as a stocking; the fill here is what gives the
        // outline its one convexity between hip and knee.
        { y: -0.248, rx: 0.078, rz: 0.067, cx: 0.012, cz: -inZ * 0.012,
          fold: 0.028, foldPhase: fp },
        { y: -0.312, rx: 0.072, rz: 0.062, cx: 0.010, cz: -inZ * 0.010,
          fold: 0.024, foldPhase: fp },
        // Last ring above the knee. Deliberately narrower than the shin part's first
        // ring, so the knee section sleeves OVER this one and no opening can ever show.
        { y: -0.368, rx: 0.065, rz: 0.056, cx: 0.006, cz: -inZ * 0.008,
          fold: 0.014, foldPhase: fp },
      ];
      /** Half-angle of the inner gusset. theta = inZ*PI/2 is the face of the other leg. */
      const GUSSET = 1.02;
      const gA = inZ * Math.PI * 0.5 - GUSSET;
      const gB = inZ * Math.PI * 0.5 + GUSSET;
      // Two arcs of ONE surface: they share their end columns exactly, because both
      // evaluate `loftPoint` at the same theta on the same rings, so there is no seam to
      // leak through and no z-fighting to author around.
      th.loft(legRings, 10, K.trouserIn, true, gA, gB);
      th.loft(legRings, 20, K.trouser, true, gB, gA + Math.PI * 2);
      const proud = (k: number, dr: number): LoftRing => ({
        ...legRings[k], rx: legRings[k].rx + dr, rz: legRings[k].rz + dr,
      });

      // Welts, the full length of the leg: one down the outer side seam, and one down each
      // edge of the gusset. Each stands 5.5 mm proud of the cloth — a crest that takes the
      // key and a valley beside it that does not, which is a form edge where the old
      // sleeve had nothing at all. They were twice this once and the pair squared off the
      // leg's silhouette from behind; a seam is a line on a surface, and the moment it
      // changes the outline it has stopped being one.
      th.surf(SURF.fabricTight[0], SURF.fabricTight[1]);
      for (const ang of [gA, gB, -inZ * Math.PI * 0.5]) {
        for (let k = 1; k <= 6; k++) {
          loftPoint(legRings[k], ang, 0.0022, _sa);
          loftPoint(legRings[k + 1], ang, 0.0022, _sb);
          th.seg(_sa.x, _sa.y, _sa.z, _sb.x, _sb.y, _sb.z, 0.0055, K.trouserHem, 0.0055, 6);
        }
      }

      // The seat does not stop at the hip. A yoke panel in the seat's own value runs off
      // the back of the shorts and down onto the back of the leg, ending in a curved seam
      // — so the dark mass under the rider is a *panel of this garment* rather than a
      // separate ellipsoid the leg happens to start beneath. That was the first item in
      // the critique's stack and this is what removes it.
      th.surf(SURF.fabric[0], SURF.fabric[1]);
      const yokeLo: LoftRing = {
        y: -0.072, rx: 0.084, rz: 0.088, cx: 0.006, fold: 0.016, foldPhase: fp,
        drop: 0.028, dropAt: Math.PI,
      };
      th.loft(
        [proud(1, 0.0035), proud(2, 0.0035), yokeLo],
        14, K.trouserIn, false, Math.PI - 1.05, Math.PI + 1.05,
      );
      th.surf(SURF.fabricTight[0], SURF.fabricTight[1]);
      for (let k = 0; k < 7; k++) {
        loftPoint(yokeLo, Math.PI - 1.05 + (2.10 * k) / 7, 0.003, _sa);
        loftPoint(yokeLo, Math.PI - 1.05 + (2.10 * (k + 1)) / 7, 0.003, _sb);
        th.seg(_sa.x, _sa.y, _sa.z, _sb.x, _sb.y, _sb.z, 0.0058, K.trouserHem, 0.0058, 6);
      }

      // Thigh pocket, outer front. Small, but it is the one thing on the lower body that
      // stands off the surface and casts across it, and it is asymmetric with the yoke
      // behind it — which is most of what stops a leg reading as a solid of revolution.
      th.surf(SURF.fabric[0], SURF.fabric[1]);
      loftPoint(legRings[4], inZ * (-Math.PI * 0.5 + 0.38), 0, _sa);
      th.blob(_sa.x + 0.006, _sa.y + 0.004, _sa.z * 0.76, 0.050, 0.058, 0.036,
        K.trouser, 10, 0.05);
      th.surf(SURF.fabricTight[0], SURF.fabricTight[1]);
      loftPoint(legRings[3], inZ * (-Math.PI * 0.5 + 0.72), 0.004, _sb);
      loftPoint(legRings[3], inZ * (-Math.PI * 0.5 + 0.04), 0.004, _sc);
      th.seg(_sb.x, _sb.y - 0.012, _sb.z, _sc.x, _sc.y - 0.012, _sc.z,
        0.0072, K.trouserHem, 0.0072, 6);

      // The leg inside the cloth — the base layer, not skin. Nothing should ever see it,
      // and that is exactly why it is the layer's own value: if a fold does open, what
      // shows is a dark inner garment rather than a flash of a completely different hue.
      // It is narrower than it was (0.060 -> 0.046): with the trouser drawn in laterally
      // to open the gap, a 0.060 cylinder on the bone axis pokes straight out through the
      // cloth's new inner face and hangs a mid-grey tube in the slot the whole exercise
      // exists to open.
      th.surf(SURF.fabricTight[0], SURF.fabricTight[1]);
      th.seg(0, -0.150, 0, 0, -S.thigh + 0.020, 0, 0.046, K.base, 0.038);
      th.shade(0, -0.014, 0, 0.108, 0.40);      // hip crease, matching the torso's
      th.shade(0.006, -0.086, 0, 0.074, 0.34);       // under the yoke seam
      th.shade(_sa.x, _sa.y - 0.060, _sa.z * 0.9, 0.066, 0.34); // under the pocket
      // The inner face, top and bottom. This is the other half of the gap fix: the two
      // surfaces that now face each other across 80 mm of air are the two the sun never
      // reaches, so they carry the hardest bake on the lower body. Without it the gusset
      // is a lighter panel than it should be at exactly the moment it matters — a rider's
      // back is lit by ambient, and ambient does not know there is a leg in the way.
      th.shade(0.004, -0.070, inZ * 0.092, 0.104, 0.46);
      th.shade(0.004, -0.210, inZ * 0.088, 0.096, 0.40);
      th.shade(0.004, -0.330, inZ * 0.082, 0.086, 0.34);
      // Above the knee, where the cloth gathers as the joint folds. It is the crease that
      // makes the knee read as a knee rather than as the place two tubes meet.
      th.shade(0.010, -0.344, 0, 0.078, 0.36);
      skin.part(th.build(), iThigh[i], hipRamp);

      // --- the lower leg: the trouser continues, and the knee is a JOINT ---------------
      // Everything on this bone used to be "a single lit cone from knee to a black ankle
      // band to a black shoe, with no calf, no ankle, no sock and no cuff". Four things
      // fix that and every one of them is geometry:
      //
      //   - the trouser carries on down it, in two panels: a knee section 23 luma darker
      //     (which is where a real trouser is reinforced, and which is what the pad
      //     underneath reads as) and a lower leg one step lighter than the thigh;
      //   - it has a CALF. The section drifts 5 mm rearward and swells between the knee
      //     and mid-shin, so the outline is not a straight taper;
      //   - it ENDS. A rolled cuff with a lit crest, a shaded under-lip and an inner wall
      //     back to the leg, plus an adjuster tab on the outside — a leg that stops in a
      //     hem is the difference between a trouser and a stocking;
      //   - and the top of it belongs to the THIGH. The ramp hands the first 88 mm to the
      //     thigh bone, so the cloth bends through the knee. The old build had no blend at
      //     any joint below the shoulder, which is why the leg read as a stack.
      const kneeRamp: SkinRamp = {
        bone: iThigh[i],
        ox: 0, oy: 0, oz: 0,
        dx: 0, dy: -1, dz: 0,
        d0: -0.088, d1: 0.088,
      };
      const sh = new PartBuilder();
      sh.underShade(0.26);
      // The leg inside, again in the base layer's value.
      sh.surf(SURF.fabricTight[0], SURF.fabricTight[1]);
      sh.seg(0, -0.02, 0, 0, -S.shin + 0.030, 0, 0.042, K.base, 0.030);
      // WAVE 4: narrowed laterally and drifted outboard on the same reasoning as the
      // thigh. The gap between the legs has to run all the way to the ankle or it reads as
      // a hole in the middle of one mass rather than as two legs — and below the knee is
      // where the chase camera has the clearest look through, because the bike is thin
      // there and the trail is behind it.
      const shinRings: LoftRing[] = [
        // Sleeves over the thigh part's last ring (0.065 / 0.056) with room to spare.
        { y: 0.078, rx: 0.073, rz: 0.062, cx: 0.007, cz: -inZ * 0.006 },
        { y: 0.022, rx: 0.078, rz: 0.064, cx: 0.013, cz: -inZ * 0.008 },
        // The knee itself: the widest point of the leg, pushed forward, because that is
        // what a pad under cloth does.
        { y: -0.032, rx: 0.080, rz: 0.066, cx: 0.015, cz: -inZ * 0.008 },
        // Panel seam.
        { y: -0.088, rx: 0.072, rz: 0.062, cx: 0.006, cz: -inZ * 0.008,
          fold: 0.012, foldPhase: fp },
        // The calf.
        { y: -0.155, rx: 0.069, rz: 0.060, cx: -0.005, cz: -inZ * 0.007,
          fold: 0.020, foldPhase: fp },
        { y: -0.228, rx: 0.062, rz: 0.055, cx: -0.006, cz: -inZ * 0.005,
          fold: 0.024, foldPhase: fp },
        { y: -0.296, rx: 0.055, rz: 0.049, cx: -0.001, cz: -inZ * 0.003,
          fold: 0.018, foldPhase: fp },
        // Cuff crest, hanging 8 mm lower at the front where a trouser sits on the shoe.
        { y: -0.344, rx: 0.052, rz: 0.046, cx: 0.002, fold: 0.010, foldPhase: fp,
          drop: 0.008, dropAt: 0 },
        // ...and the profile turns back: under-lip, then inner wall home to the leg.
        { y: -0.334, rx: 0.046, rz: 0.041, cx: 0.002, drop: 0.008, dropAt: 0 },
        { y: -0.316, rx: 0.038, rz: 0.034, cx: 0.002 },
      ];
      sh.surf(SURF.fabric[0], SURF.fabric[1]);
      // Same two-arc split as the thigh, so the inseam value break is continuous from the
      // waistband to the cuff instead of stopping at the knee.
      sh.loft(shinRings.slice(0, 4), 8, K.trouserIn, false, gA, gB);
      sh.loft(shinRings.slice(0, 4), 16, K.trouserKnee, false, gB, gA + Math.PI * 2);
      sh.loft(shinRings.slice(3), 8, K.trouserIn, false, gA, gB);
      sh.loft(shinRings.slice(3), 16, K.trouserLo, false, gB, gA + Math.PI * 2);
      const proudS = (k: number, dr: number): LoftRing => ({
        ...shinRings[k], rx: shinRings[k].rx + dr, rz: shinRings[k].rz + dr,
      });
      // Panel seam between the two, and the cuff binding: both built off the same rings
      // the surface was, so neither can float or sink.
      sh.surf(SURF.fabricTight[0], SURF.fabricTight[1]);
      sh.loft([
        { ...proudS(3, 0.0035), y: -0.080 }, { ...proudS(3, 0.0035), y: -0.098 },
      ], 24, K.trouserHem);
      sh.loft([proudS(6, 0.003), proudS(7, 0.003), proudS(8, 0.003)], 24, K.trouserKnee);
      // Outer side seam and gusset seams down the shin, and the cuff's adjuster tab on
      // the outer side.
      for (const ang of [gA, gB, -inZ * Math.PI * 0.5]) {
        for (let k = 3; k <= 6; k++) {
          loftPoint(shinRings[k], ang, 0.0022, _sa);
          loftPoint(shinRings[k + 1], ang, 0.0022, _sb);
          sh.seg(_sa.x, _sa.y, _sa.z, _sb.x, _sb.y, _sb.z, 0.0052, K.trouserHem, 0.0052, 6);
        }
      }
      loftPoint(shinRings[7], -inZ * Math.PI * 0.5, 0.006, _sc);
      sh.box(0.034, 0.024, 0.016, _sc.x, _sc.y + 0.016, _sc.z, K.trouserKnee, 0.10);
      // Ankle: the sock the cuff sits on, running into the shoe.
      sh.blob(0, -S.shin + 0.026, 0, 0.042, 0.046, 0.038, K.trim);
      sh.shade(0.010, -0.092, 0, 0.076, 0.40);        // the panel seam's own valley
      sh.shade(0.004, -0.322, 0, 0.062, 0.52);        // inside the cuff
      sh.shade(0, -S.shin + 0.058, 0, 0.070, 0.40);   // ankle into the shoe
      // The inner face again, so the shadow in the slot runs to the ankle.
      sh.shade(0.004, -0.090, inZ * 0.080, 0.088, 0.40);
      sh.shade(0.004, -0.240, inZ * 0.072, 0.080, 0.34);
      skin.part(sh.build(), iShin[i], kneeRamp);
    }
  }

  /**
   * The bind pose: the rider standing on the pedals with their hands on the bars, solved
   * with every dynamic term at zero. It is the neutral branch of `update()` and nothing
   * else — same reach solve, same IK, same `aim()` — so the pose the weights are
   * authored against is a pose the rig can actually reach.
   */
  private bindPose(c: Cockpit): void {
    const S = RiderSize;
    this.hip.set(POSE.hipX, POSE.hipY, 0);
    this.gripMid.addVectors(c.gripL, c.gripR).multiplyScalar(0.5);
    this.shoTarget.set(this.gripMid.x + POSE.reachX, this.gripMid.y + POSE.reachY,
      this.gripMid.z);
    this.tmp.subVectors(this.shoTarget, this.hip);
    const tl = this.tmp.length() || 1e-4;
    this.sho.copy(this.hip).addScaledVector(this.tmp, S.torso / tl);
    this.bodyUp.copy(this.tmp).divideScalar(tl);
    this.bodyRight.set(0, 0, 1);
    this.bodyRight.addScaledVector(this.bodyUp, -this.bodyRight.dot(this.bodyUp));
    this.bodyRight.normalize();
    this.bodyFwd.crossVectors(this.bodyUp, this.bodyRight);

    this.torso.position.copy(this.hip);
    _m.makeBasis(this.bodyFwd, this.bodyUp, this.bodyRight);
    this.torso.quaternion.setFromRotationMatrix(_m);

    this.head.position.copy(this.sho)
      .addScaledVector(this.bodyUp, S.neck)
      .addScaledVector(this.bodyFwd, 0.055);
    _bnX.set(Math.cos(0.10), Math.sin(0.10), 0);
    _bnZ.copy(this.bodyRight).addScaledVector(_bnX, -this.bodyRight.dot(_bnX)).normalize();
    _bnY.crossVectors(_bnZ, _bnX);
    _m.makeBasis(_bnX, _bnY, _bnZ);
    this.head.quaternion.setFromRotationMatrix(_m);

    for (let i = 0; i < 2; i++) {
      const sgn = i === 0 ? -1 : 1;
      const grip = i === 0 ? c.gripL : c.gripR;
      this.limbRoot.copy(this.sho).addScaledVector(this.bodyRight, sgn * S.shoulderHalf);
      this.pole.set(-0.80, -0.40, sgn * 0.72).normalize();
      solveTwoBone(this.limbRoot, grip, S.upperArm, S.foreArm, this.pole, this.joint, this.tip);
      this.upperArm[i].aim(this.limbRoot, this.joint, this.fwdRef);
      this.foreArm[i].aim(this.joint, this.tip, this.fwdRef);

      const pedal = i === 0 ? c.pedalL : c.pedalR;
      this.limbTarget.set(pedal.x - 0.012, pedal.y + 0.078, pedal.z);
      this.limbRoot.copy(this.hip)
        .addScaledVector(this.bodyRight, sgn * POSE.hipSplay)
        .addScaledVector(this.bodyUp, -0.02);
      this.pole.set(1.0, 0.16, sgn * 0.34).normalize();
      solveTwoBone(this.limbRoot, this.limbTarget, S.thigh, S.shin, this.pole,
        this.joint, this.tip);
      this.thigh[i].aim(this.limbRoot, this.joint, this.fwdRef);
      this.shin[i].aim(this.joint, this.tip, this.fwdRef);
    }
  }

  /**
   * Solve the whole rider for this frame. `s` is read-only sim state; `c` is the bike's
   * own cockpit resolved into the same chassis frame this rig lives in. Nothing here
   * writes to either.
   */
  update(s: BikeState, c: Cockpit, dt: number): void {
    const S = RiderSize;

    // --- what the mountain is doing ------------------------------------------------
    // Ground attitude under the bike, from the sim's own contact normals. Body english
    // has to be measured against the hill, not against the horizon: on a 30 degree
    // chute an ordinary attitude *is* 30 degrees nose down.
    const cy = Math.cos(s.yaw);
    const sy = Math.sin(s.yaw);
    let gnx = s.front.ground.nx + s.rear.ground.nx;
    let gny = s.front.ground.ny + s.rear.ground.ny;
    let gnz = s.front.ground.nz + s.rear.ground.nz;
    const gl = Math.hypot(gnx, gny, gnz) || 1;
    gnx /= gl; gny /= gl; gnz /= gl;
    const along = gnx * cy + gnz * sy;
    const across = -gnx * sy + gnz * cy;
    const groundPitch = -Math.atan2(along, Math.max(gny, 1e-3));
    const bank = Math.asin(clamp(across, -1, 1));

    this.steepSmooth = damp(this.steepSmooth, clamp01(-groundPitch / 0.55), 5, dt);
    const tuck = clamp01((s.speed - POSE.tuckLo) / (POSE.tuckHi - POSE.tuckLo));
    this.tuckSmooth = damp(this.tuckSmooth, tuck, 3, dt);

    // --- the fall ---------------------------------------------------------------------
    // See `CRASH`. Smoothstepped rather than used raw so the let-go has no corner in it;
    // a linear blend on a 0.1 s ramp reads as a snap even though it is the right duration.
    this.crashMix = damp(this.crashMix, s.crashed ? 1 : 0,
      s.crashed ? CRASH.inRate : CRASH.outRate, dt);
    const cm = this.crashMix < 1e-3 ? 0 : this.crashMix * this.crashMix * (3 - 2 * this.crashMix);
    // Riding pose terms all fade out with the fall: a rider on the floor is not tucking
    // for speed and is not reading the gradient ahead.
    const tk = this.tuckSmooth * (1 - cm);

    // --- what the limbs are carrying -------------------------------------------------
    // Arms are driven by the front axle's normal load, because that is the load which
    // arrives at the hands; legs by the whole-bike specific force, because that is what
    // the feet are standing on and it is already slope-corrected. Both come straight out
    // of the sim rather than being differenced off positions, so they are clean signals
    // with no frame-rate noise in them. Airborne, both go to zero and every limb
    // straightens; on a bottom-out both go past 2 and the rider folds into the bike.
    // clamp() passes NaN straight through, and a NaN that reaches a spring stays there
    // for the rest of the session, so the guard is on the way in rather than on the way
    // out.
    const fl = Number.isFinite(s.front.load) ? s.front.load : STATIC_FRONT;
    const gf = Number.isFinite(s.gForce) ? s.gForce : 1;
    this.armDrive = damp(this.armDrive, clamp(fl / STATIC_FRONT, 0, 4) - 1, 26, dt);
    this.legDrive = damp(this.legDrive, clamp(gf, 0, 4) - 1, 26, dt);

    // Cornering splits the two sides. In a coordinated turn the lateral acceleration is
    // g*tan(lean), so lean is the whole measurement: the outside leg takes the load and
    // drives straight into the pedal, the inside one folds up under the rider, and the
    // arms do the mirror of that as the rider pushes the bar down on the inside.
    const latG = clamp(Math.tan(clamp(s.lean, -1.15, 1.15)), -2.2, 2.2);
    const latM = Math.abs(latG);
    const inside = s.lean > 0 ? 1 : 0;
    const armBase = this.armDrive >= 0
      ? this.armDrive * LIMB.armComp : this.armDrive * LIMB.armExt;
    const legBase = this.legDrive >= 0
      ? this.legDrive * LIMB.legComp : this.legDrive * LIMB.legExt;
    for (let i = 0; i < 2; i++) {
      // out = +1 on the outside of the turn, -1 on the inside, 0 when upright.
      const out = i === inside ? -1 : 1;
      this.armT[i] = clamp(armBase + out * latM * LIMB.armLat,
        -LIMB.armExtMax, LIMB.armCompMax);
      this.legT[i] = clamp(legBase - out * latM * LIMB.legLat,
        -LIMB.legExtMax, LIMB.legCompMax);
    }

    // Integrate. Substepped because an explicit spring this stiff goes unstable at the
    // 100 ms dt a stalled frame can hand us, and a rider who explodes on a hitch is a
    // worse bug than a rider who does not move at all.
    {
      const n = Math.max(1, Math.min(24, Math.ceil(dt / LIMB.sub)));
      const h = dt / n;
      const w2 = LIMB.omega * LIMB.omega;
      const cd = 2 * LIMB.zeta * LIMB.omega;
      for (let k = 0; k < n; k++) {
        for (let i = 0; i < 2; i++) {
          this.armV[i] = clamp(this.armV[i] + (w2 * (this.armT[i] - this.armX[i]) - cd * this.armV[i]) * h, -6, 6);
          this.armX[i] = clamp(this.armX[i] + this.armV[i] * h, -LIMB.armExtMax * 1.3, LIMB.armCompMax * 1.3);
          this.legV[i] = clamp(this.legV[i] + (w2 * (this.legT[i] - this.legX[i]) - cd * this.legV[i]) * h, -6, 6);
          this.legX[i] = clamp(this.legX[i] + this.legV[i] * h, -LIMB.legExtMax * 1.3, LIMB.legCompMax * 1.3);
        }
      }
    }
    this.armAvg = (this.armX[0] + this.armX[1]) * 0.5;
    this.legAvg = (this.legX[0] + this.legX[1]) * 0.5;
    // How straight the limbs are allowed to get. A limb carrying nothing may reach 97% of
    // its own length; one carrying two riders' worth of load may not, because a knee at
    // 97% under that load is a knee that has just been broken. This is what actually lets
    // the legs come out long in the air instead of the hip clamp holding them folded.
    const legStretch = clamp01(-this.legAvg / LIMB.legExtMax);
    const armStretch = clamp01(-this.armAvg / LIMB.armExtMax);

    // Counter-lean. In a berm the bank is already holding the cornering force, so the
    // rider lies down with the bike; on the flat the ground holds none of it and the
    // rider keeps their mass over the tires by staying upright. bermAlign is how much
    // of the lean the ground has taken.
    const bermAlign = clamp01((bank * s.lean) / Math.max(s.lean * s.lean, 0.025));
    // ...and it goes to zero in a crash: counter-lean is an act of balance, and a rider on
    // the floor is not balancing. Without this the rig keeps holding the torso 46% upright
    // against a bike lying at 80 degrees, which is most of the "rigid mannequin" read.
    const counterTarget = -s.lean * POSE.counterLean * (1 - bermAlign) * (1 - cm);
    this.counterSmooth = damp(this.counterSmooth, counterTarget, 8, dt);
    const phi = this.counterSmooth;
    const cphi = Math.cos(phi);
    const sphi = Math.sin(phi);
    const cphiS = Math.cos(phi * POSE.shoulderRoll);
    const sphiS = Math.sin(phi * POSE.shoulderRoll);

    // --- hips ----------------------------------------------------------------------
    const fore = s.riderFore * POSE.foreGain;
    const back = Math.max(0, -fore);
    const leanAbs = Math.abs(s.lean);
    let hipX = POSE.hipX + fore + leanAbs * 0.045 - tk * POSE.tuckHipBack - cm * CRASH.hipBack;
    let hipY = POSE.hipY
      - cm * CRASH.hipDrop
      - s.bodySquash * 0.90
      // The legs' own spring, on top of what the sim's body coordinate already did.
      // bodySquash is a 28 kg mass on a stiff spring and moves ±0.05 m; this is the
      // rest of the rider and it moves three times that, which is the difference
      // between "the pose wobbles" and "the rider is absorbing the hit".
      - this.legAvg
      - back * POSE.backDrop
      - tk * POSE.tuckHip
      - leanAbs * 0.05;
    // Getting low means getting *behind* the saddle, not through it. Any drop the
    // saddle is in the way of is spent going backwards instead, which is both the fix
    // for the interpenetration and, exactly, the move a rider makes.
    //
    // The `(1 - behind)` is load-bearing and was not there at first: without it the
    // push-back stacks on top of a weight shift that has already cleared the saddle, and
    // at full stick the hips ended up 0.85 m behind the bottom bracket — a fifth of a
    // metre past what a leg can reach, with the foot hanging in space behind the pedal.
    const clearY = c.saddleTop + 0.086;
    let behind = clamp01((c.saddleX - 0.10 - hipX) / 0.22);
    hipX -= Math.max(0, clearY - hipY) * 1.7 * (1 - behind);
    behind = clamp01((c.saddleX - 0.10 - hipX) / 0.22);
    hipY = Math.max(hipY, lerp(clearY, c.bbY + 0.32, behind));
    // Roll the hips about the pedal axis. This is the visible half of counter-lean:
    // hips and knees go to the outside while the shoulders barely move.
    const hy = hipY - c.bbY;
    this.hip.set(hipX, c.bbY + hy * cphi, hy * sphi + s.riderLat * 0.22);

    // Legs are the hard stop on body english: however far back the sim says the rider's
    // mass has gone, the hips cannot end up further from a pedal than a leg is long.
    // Enforced on the hips rather than papered over in the IK, because the alternative
    // is a straight leg with the foot floating short of the platform, which is exactly
    // the sliding-feet artefact this rig exists to remove.
    const legLim = (RiderSize.thigh + RiderSize.shin) * (0.905 + legStretch * 0.072);
    for (let i = 0; i < 2; i++) {
      const p = i === 0 ? c.pedalL : c.pedalR;
      this.tmp.subVectors(this.hip, p);
      const d = this.tmp.length();
      // Faded out by the fall: once the feet are off the pedals there is no pedal to be
      // in reach of, and leaving this on drags the hips back toward a cockpit the rider
      // has just let go of.
      if (d > legLim) this.hip.addScaledVector(this.tmp, ((legLim - d) / d) * (1 - cm));
    }

    // --- shoulders -----------------------------------------------------------------
    // Anchored to the bars, not to the rider: the hands have to end up on the grips, so
    // the torso angle is a consequence, not an input. This is what makes weight-back on
    // a steep automatically flatten the back and extend the arms.
    this.gripMid.addVectors(c.gripL, c.gripR).multiplyScalar(0.5);
    const reachX = POSE.reachX - back * POSE.reachBack;
    // The arms' own spring rides here: under load the shoulders settle toward the bars
    // and the elbows fold, unloaded they rise and the arms come straight. Vertical is
    // very nearly the arm axis at this cockpit height (0.405 up against 0.155 back), so
    // one number does the job without needing a per-arm rotation.
    const reachY = POSE.reachY - tk * POSE.tuckReach - this.steepSmooth * 0.035 - this.armAvg;
    this.shoTarget.set(this.gripMid.x + reachX, this.gripMid.y + reachY, this.gripMid.z);
    const sh = this.shoTarget.y - c.bbY;
    this.shoTarget.set(this.shoTarget.x, c.bbY + sh * cphiS, this.shoTarget.z + sh * sphiS);
    // Never further from the bars than an arm can span, whatever the weight shift asked
    // for. Everything past this point only tightens it.
    // A back has a floor. Full tuck and a big rearward weight shift both push the
    // shoulders down and they stack: at 62 km/h on a steep pitch the two together laid
    // the torso flat along the top tube, which stops reading as a rider and starts
    // reading as a body draped over a bike. ~12 degrees above the frame is the stop.
    if (this.shoTarget.y < this.hip.y + 0.105) this.shoTarget.y = this.hip.y + 0.105;
    this.tmp.subVectors(this.shoTarget, this.gripMid);
    /** How far a hand may sit from its shoulder. Unloaded arms may come straighter. */
    const armLim = ARM_MAX * (0.945 + armStretch * 0.035);
    const dLim = ARM_MAX * (0.84 + armStretch * 0.055);
    const dNow = this.tmp.length();
    if (dNow > dLim) this.shoTarget.copy(this.gripMid).addScaledVector(this.tmp, dLim / dNow);
    if (cm > 0) {
      // THE change that removes the mannequin. Everything above this line makes the
      // shoulders a function of where the BARS are — that is the whole design of this rig
      // and it is why weight-back automatically flattens the back. It is also why a crash
      // looked like a shop dummy falling over: the back stayed straight because it was
      // still solving a reach. Rounding the back is not an animation laid on top; it is
      // what happens the moment the shoulders stop being anchored to a cockpit.
      this.crashT.set(this.hip.x + CRASH.shoFwd,
        this.hip.y + S.torso * CRASH.torsoFold, this.hip.z);
      this.shoTarget.lerp(this.crashT, cm);
    }

    // The torso is a fixed length, so aim it at that target and then, if either hand is
    // out of reach, walk the target toward the bars until it is not. The correction is
    // monotone, so a handful of passes converges to within a millimetre or two.
    for (let pass = 0; pass < 5; pass++) {
      this.tmp.subVectors(this.shoTarget, this.hip);
      const tl = this.tmp.length() || 1e-4;
      this.sho.copy(this.hip).addScaledVector(this.tmp, S.torso / tl);
      this.bodyUp.copy(this.tmp).divideScalar(tl);
      this.bodyRight.set(0, -sphiS, cphiS);
      this.bodyRight.addScaledVector(this.bodyUp, -this.bodyRight.dot(this.bodyUp));
      if (this.bodyRight.lengthSq() < 1e-6) this.bodyRight.set(0, 0, 1);
      this.bodyRight.normalize();
      this.bodyFwd.crossVectors(this.bodyUp, this.bodyRight);

      if (pass === 4) break;
      let worst = 0;
      for (let i = 0; i < 2; i++) {
        const sgn = i === 0 ? -1 : 1;
        this.tmp.copy(this.sho).addScaledVector(this.bodyRight, sgn * S.shoulderHalf);
        const d = this.tmp.distanceTo(i === 0 ? c.gripL : c.gripR);
        if (d > worst) worst = d;
      }
      const excess = worst - armLim;
      if (excess <= 0) break;
      this.tmp.subVectors(this.gripMid, this.shoTarget);
      const tn = this.tmp.length() || 1e-4;
      // Faded out by the fall for the same reason as the leg limit: this correction walks
      // the shoulders back toward the bars, which is exactly what must not happen once
      // the hands have let go of them.
      this.shoTarget.addScaledVector(this.tmp, (Math.min(excess * 1.4, tn * 0.6) / tn) * (1 - cm));
    }

    this.torso.position.copy(this.hip);
    _m.makeBasis(this.bodyFwd, this.bodyUp, this.bodyRight);
    this.torso.quaternion.setFromRotationMatrix(_m);

    // --- head ----------------------------------------------------------------------
    // Eyes down the trail. The helmet lives in chassis space, so a look pitch of zero
    // already points down the hill on a descent; the rest is the rider lifting their
    // eyes further ahead the steeper it gets, and giving back any nose-dive.
    const relPitch = clamp(s.pitch - groundPitch, -0.8, 0.8);
    this.head.position.copy(this.sho)
      // The neck is the last spring in the chain: the helmet sinks a little further than
      // the shoulders under load and floats a little higher off them in the air. It is a
      // 30 mm effect and it is worth every one of those millimetres, because the helmet
      // is the brightest thing on the rider and the eye tracks it.
      .addScaledVector(this.bodyUp, S.neck - this.armAvg * 0.34)
      .addScaledVector(this.bodyFwd, 0.055 + tk * POSE.tuckHead);
    // Eyes stay down the trail through the tuck: the chest drops, the chin does not.
    // ...until the rider is not riding, at which point the chin goes to the chest. Applied
    // after the riding clamp so a crash can take the head below where any riding pose is
    // allowed to — that is the point of it.
    const lookPitch = clamp(
      clamp(0.10 + this.steepSmooth * 0.28 + tk * 0.20 - relPitch * 0.45, -0.5, 0.7)
      - cm * CRASH.lookDown, -0.95, 0.7);
    _bnX.set(Math.cos(lookPitch), Math.sin(lookPitch), 0);
    _bnZ.copy(this.bodyRight).addScaledVector(_bnX, -this.bodyRight.dot(_bnX));
    if (_bnZ.lengthSq() < 1e-6) _bnZ.set(0, 0, 1);
    _bnZ.normalize();
    _bnY.crossVectors(_bnZ, _bnX);
    _m.makeBasis(_bnX, _bnY, _bnZ);
    this.head.quaternion.setFromRotationMatrix(_m);

    // --- arms ----------------------------------------------------------------------
    // The two arms only share the *average* of their springs, because that part of the
    // travel is the torso moving and the torso is one rigid thing. What is left over is
    // the difference between the two sides, and it is applied here at the shoulder, in
    // the direction of that hand's own grip — a few centimetres, entirely inside the
    // deltoid mass, so nothing gaps and the two elbows genuinely end up at different
    // angles in a corner. The grip itself is never moved: a hand that leaves the bar to
    // express load has traded one artefact for a worse one.
    for (let i = 0; i < 2; i++) {
      const sgn = i === 0 ? -1 : 1;
      const grip = i === 0 ? c.gripL : c.gripR;
      // Where this hand is going. Riding: the grip, always, exactly. Down: up in front of
      // the face with the elbow in — the one move every rider makes going over the bars,
      // and the only pose that is true once the bars are lying on the hillside.
      this.limbTarget.copy(grip);
      if (cm > 0) {
        this.crashT.copy(this.sho)
          .addScaledVector(this.bodyUp, CRASH.handUp)
          .addScaledVector(this.bodyFwd, CRASH.handFwd)
          .addScaledVector(this.bodyRight, sgn * CRASH.handOut);
        this.limbTarget.lerp(this.crashT, cm);
      }
      this.limbRoot.copy(this.sho).addScaledVector(this.bodyRight, sgn * S.shoulderHalf);
      const dArm = this.armX[i] - this.armAvg;
      if (dArm !== 0) {
        this.tmp.subVectors(this.limbTarget, this.limbRoot);
        const tn = this.tmp.length() || 1e-4;
        this.limbRoot.addScaledVector(this.tmp, dArm / tn);
        // ...but never past what the arm can span. solveTwoBone clamps an unreachable
        // target instead of stretching, which would take the hand off the grip — the
        // exact artefact this whole rig exists to prevent — so the clamp goes here.
        this.tmp.subVectors(this.limbRoot, this.limbTarget);
        const dn = this.tmp.length();
        if (dn > armLim) this.limbRoot.addScaledVector(this.tmp, (armLim - dn) / dn);
      }
      // Elbows out and back — the attack position, and the only bend direction that
      // keeps a forearm out of the head tube. They lift and flare as the arms take load
      // (bracing) and as the rider tucks, and drop in toward the body in the air.
      this.pole.set(-0.80, -0.40 + this.armAvg * 2.2 + tk * 0.30, sgn * 0.72).normalize();
      solveTwoBone(this.limbRoot, this.limbTarget, S.upperArm, S.foreArm,
        this.pole, this.joint, this.tip);
      this.upperArm[i].aim(this.limbRoot, this.joint, this.fwdRef);
      this.foreArm[i].aim(this.joint, this.tip, this.fwdRef);
      // The two halves of the same audit: riding, the solved hand must be ON the grip;
      // down, it must have left it. Both are measured on the pose that was actually
      // solved, after every clamp, which is the only place either claim is testable.
      //
      // The let-go half is gated on `cm > 0.5`, NOT on `s.crashed`, and the first exercised
      // run of it is why. Written against the raw flag it reported VIOLATED with a worst of
      // 0.3 mm on 6.25% of samples — and every one of those samples was inside the 0.13 s
      // the hands take to leave the bars. The blend is the point (`CRASH.inRate` 13; a rider
      // lets go in about a tenth of a second, and a one-frame jump to arms-up is a snap),
      // so an invariant that counts the ramp as a failure is measuring the wrong instant.
      // It is gated at the halfway point of the fall instead.
      //
      // This is a sharpening and not a fudge, and the test of that is whether it can still
      // fail on the defect it was written for: a rig that does not read `crashed` at all
      // still reaches `cm > 0.5` (the blend runs regardless) and still reports a hand welded
      // to a grip that is lying on the hillside. What it no longer reports is the let-go
      // itself.
      if (cm > 0.5) CRASH_LETGO.observeAt(this.tip.distanceTo(grip), s.x, s.y, s.z);
      else if (cm === 0) HAND_GAP.observeAt(this.tip.distanceTo(grip), s.x, s.y, s.z);
    }

    // --- legs ----------------------------------------------------------------------
    for (let i = 0; i < 2; i++) {
      const sgn = i === 0 ? -1 : 1;
      const pedal = i === 0 ? c.pedalL : c.pedalR;
      // The ankle, not the pedal: the foot has thickness and the shoe sits on the
      // platform, so the shin has to stop above it or the rider stands on their bones.
      this.limbTarget.set(pedal.x - 0.012, pedal.y + 0.078, pedal.z);
      if (cm > 0) {
        // ...and in the fall the feet leave the platform and the knees draw up. A crashed
        // rider is not standing on anything, and feet welded to pedals is the lower-body
        // half of the mannequin.
        this.crashT.copy(this.hip)
          .addScaledVector(this.bodyFwd, CRASH.footFwd)
          .addScaledVector(this.bodyUp, -CRASH.footDown)
          .addScaledVector(this.bodyRight, sgn * CRASH.footOut);
        this.limbTarget.lerp(this.crashT, cm);
      }
      this.limbRoot.copy(this.hip)
        .addScaledVector(this.bodyRight, sgn * POSE.hipSplay)
        .addScaledVector(this.bodyUp, -0.02);
      // Same split as the arms: the shared part already moved the hips, this is the
      // difference between the two legs. It is what makes the outside leg drive long
      // into the pedal while the inside one stays folded up under the rider.
      const dLeg = this.legX[i] - this.legAvg;
      if (dLeg !== 0) {
        this.tmp.subVectors(this.limbTarget, this.limbRoot);
        const tn = this.tmp.length() || 1e-4;
        this.limbRoot.addScaledVector(this.tmp, dLeg / tn);
      }
      // Same reason as the arms: a hip the leg cannot reach from is a foot floating off
      // the pedal, so clamp the root rather than let the IK clamp the target.
      this.tmp.subVectors(this.limbRoot, this.limbTarget);
      const dn = this.tmp.length();
      if (dn > legLim) this.limbRoot.addScaledVector(this.tmp, (legLim - dn) / dn);
      // Knees forward, and the inside knee further out — the single most recognisable
      // thing a cornering mountain biker does. The standing-straight number is small on
      // purpose: at 0.56 the knee pads ended up wider than the elbows head-on, which
      // reads as bow-legged rather than as an attack stance. The corner term is where
      // the knees are supposed to go wide, and it is untouched.
      // The corner term is read off `lean`, and a crashed bike's lean runs to 80 degrees
      // and beyond, so without the fade one knee goes fully splayed for the whole fall.
      const knee = sgn * (0.34 + clamp01(s.lean * sgn * 1.6) * 0.95 * (1 - cm));
      this.pole.set(1.0, 0.16 + tk * 0.10, knee).normalize();
      solveTwoBone(this.limbRoot, this.limbTarget, S.thigh, S.shin, this.pole, this.joint, this.tip);
      this.thigh[i].aim(this.limbRoot, this.joint, this.fwdRef);
      this.shin[i].aim(this.joint, this.tip, this.fwdRef);
    }
  }
}

// ---------------------------------------------------------------------------------
// The one thing the renderer needs from the player that BikeState does not carry.
//
// Cranks must not be driven off wheel speed: a bike has a freewheel, so coasting down a
// descent with the pedals whirling is wrong in a way every rider notices instantly —
// and worse, it drags the feet round with them. Coasting, the cranks sit level and the
// outside foot drops in a corner. `pedal` is the only input needed to tell those apart.
// ---------------------------------------------------------------------------------

export const riderInput = { pedal: 0, brake: 0, manual: 0 };

registerSystem({
  id: 'rider',
  order: 30,
  fixedUpdate(_dt, _ctx, input) {
    riderInput.pedal = input.pedal;
    riderInput.brake = Math.max(input.brakeFront, input.brakeRear);
    riderInput.manual = input.manual;
  },
});
